/**
 * Every enumeration in the model, as a frozen value list plus the TypeScript union it implies.
 *
 * `erd.md` Part 3, first schema-wide decision: enumerations are CHECK constraints plus TypeScript
 * unions, never lookup tables. A lookup table earns its place only when a non-technical owner must
 * add or rename a value without a deploy, or when the value carries attributes beyond its own
 * name. None of the values below qualifies: each one is fixed by the published rate card, by law,
 * or by a code path.
 *
 * THIS FILE IS THE SINGLE DEFINITION. The schema builds its CHECK constraints from these arrays
 * (see api/src/db/schema/_shared.ts), so a value added here and nowhere else is a migration away
 * from being legal, and a value the database accepts that TypeScript does not know about cannot
 * exist. A test asserts every CHECK constraint in the migrations is built from a list here.
 *
 * Where a vocabulary already exists in the business repo it is copied VERBATIM, hyphens and all,
 * and the source is named. Inventing a synonym for a word the owner already uses is how two
 * systems end up disagreeing about what happened.
 */

/** Helper: freeze a value list and keep the literal types. */
const values = <const T extends readonly string[]>(list: T): T => Object.freeze(list) as T;

/* ------------------------------------------------------------------ *
 * A. Platform: who is signed in, and what left the building
 * ------------------------------------------------------------------ */

/**
 * The only four authorization roles. NEVER `employee`, `staff` or `team member`, in an enum, a
 * column name or a display string: `what-we-cannot-say.md` forbids those phrases on customer
 * facing surfaces, and a database string has a way of reaching a template. A house-style test
 * fails the build on any of them. Tax classification is a different column entirely.
 */
export const ROLES = values(['owner', 'manager', 'office', 'cleaner']);
export type Role = (typeof ROLES)[number];

export const USER_STATUSES = values(['active', 'suspended']);
export type UserStatus = (typeof USER_STATUSES)[number];

/**
 * THE CLEANERS' APP SIGNING IN (ops #74 item 1, `docs/plan-cleaner-app-backend.md` phases 5 and 10,
 * decisions 14 and 15). What `POST /api/auth/token` takes of the phone, `device.platform` and
 * `device.locale`, validated at the door and kept on the sign-in's audit line, never in a column: no
 * CHECK holds the platform. `push_devices.locale` (phase 9, migration `0060`) is written with the same
 * four, decided in full so that table is never rebuilt to widen it (gotcha 34). The platform here is the build's
 * own, which is not a push service's word: a device's push platform is `apns`, `fcm` or `webpush`.
 *
 * A locale is the language the person reads the app in. Ops's own sentences answer in English until
 * the office approves a translation; words a person wrote are never translated.
 */
export const APP_PLATFORMS = values(['ios', 'android', 'web']);
export type AppPlatform = (typeof APP_PLATFORMS)[number];

export const APP_LOCALES = values(['en', 'es', 'uk', 'ru']);
export type AppLocale = (typeof APP_LOCALES)[number];

/**
 * A PHONE IS TOLD (ops #74 item 4, `docs/plan-cleaner-app-backend.md` phase 9, migration `0060`). Every
 * list below is a CHECK on a table `0060` created, decided in full at creation so none is ever widened
 * by a rebuild (gotcha 34).
 *
 * `PUSH_PLATFORMS`, the push service a device is reached through, which is not the build's own word:
 * an iPhone registers `apns`, an Android phone `fcm`, the web build `webpush`.
 */
export const PUSH_PLATFORMS = values(['apns', 'fcm', 'webpush']);
export type PushPlatform = (typeof PUSH_PLATFORMS)[number];

/**
 * THE FIVE THINGS A CLEANER IS TOLD, each about one visit of theirs and each saying the day and the kind
 * and nothing else (`docs/seams/cleaner-app.md` section 11): put on it, taken off it, moved, cancelled,
 * and a note from the household landed on its sheet. No push for a call-off or a decline, which are the
 * cleaner's own news.
 */
export const PUSH_KINDS = values([
  'assignment_added',
  'assignment_removed',
  'visit_moved',
  'visit_cancelled',
  'instruction_added',
]);
export type PushKind = (typeof PUSH_KINDS)[number];

/**
 * Where one push stands, the outbox's four: `pending` waits for the drain, a failure retried included;
 * `sent`, the push service took it; `failed`, the push service refused this push for good; `dead`, it
 * will never go (the device is gone, the person is off the crew, no key is set on the host, it waited
 * a day, or five tries failed), with the reason on the row.
 */
export const PUSH_DELIVERY_STATES = values(['pending', 'sent', 'failed', 'dead']);
export type PushDeliveryState = (typeof PUSH_DELIVERY_STATES)[number];

/**
 * WHY A DEVICE IS NO LONGER TOLD ANYTHING: the person removed it, signed out of the session it was
 * registered under, registered its token again (a sign-in afresh, or the phone passed to another
 * cleaner), the push service said the token is dead, the session ended otherwise, or the person left
 * the crew. A device is revoked and never deleted (binding rule 2).
 */
export const PUSH_REVOKE_REASONS = values([
  'removed',
  'signed_out',
  're_registered',
  'platform_rejected',
  'session_ended',
  'left_the_crew',
]);
export type PushRevokeReason = (typeof PUSH_REVOKE_REASONS)[number];

/**
 * The two push services ops holds a short-lived credential for between ticks: APNs' provider token,
 * which Apple refuses to see renewed more than once in twenty minutes, and FCM's hour-long access
 * token. Web Push signs each push afresh and keeps nothing.
 */
export const PUSH_TOKEN_PLATFORMS = values(['apns', 'fcm']);
export type PushTokenPlatform = (typeof PUSH_TOKEN_PLATFORMS)[number];

/**
 * What a signed expiring link may point at, after revision 4 redefined `access_grants`. All three
 * are client facing artifacts. There is no visit sheet here, because a cleaner signs in. Behind a
 * grant there is the read, which counts her opens and the steps of the pay page she reached, and
 * since ops #46 two requests that are not reads: an invoice's link may ask Stripe for a page to pay
 * that one invoice through (`POST /api/public/:token/pay-page`), and may say how the clean went
 * (`POST /api/public/:token/feedback`).
 */
export const ACCESS_GRANT_TARGETS = values(['visit_report', 'invoice', 'home_watch_report']);
export type AccessGrantTarget = (typeof ACCESS_GRANT_TARGETS)[number];

/**
 * `read_secret` is not decoration: it is what lets Victoria answer "who saw this code" after a
 * cleaner leaves, which is the question the whole access-secret design exists to answer.
 */
export const AUDIT_ACTIONS = values([
  'create',
  'update',
  'delete',
  'restore',
  'read_secret',
  'sign_in',
  'sign_in_refused',
  'status_change',
  'handoff',
  'check',
]);
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * What leaves the building, one word per seam and direction. Decided in full by migration `0023`,
 * because this list is a CHECK and widening it later is a rebuild of the outbox (gotcha 34).
 * `phone_instruction` is an instruction down the phone seam that is not a message, a cancel, a
 * suppression or a forget (ops #23, #29 and #27), retried and parked like every push, and drained
 * since ops #29 one word of `PHONE_INSTRUCTIONS` at a time; `home_push` is the client system's seam of
 * roadmap D (ops #19), written by nothing yet. The drain sends only the kinds it has a destination for
 * (`seams/outbox.ts`), so a word here is never a post by itself.
 */
export const OUTBOX_KINDS = values([
  'lead_status_callback',
  'message_handoff',
  'phone_instruction',
  'home_push',
]);
export type OutboxKind = (typeof OUTBOX_KINDS)[number];

/**
 * THE INSTRUCTIONS OTHER THAN A SEND that the phone system's contract takes, each the `instruction`
 * of a `phone_instruction` outbox row: the four of 2026-09-16, and `unsuppress_number`, live there
 * since 2026-10-16 (`faithfully-kept-phone#26`). A list and a type, never a CHECK: the word lives
 * inside the row's payload, so the fifth cost no rebuild. Ops queues `cancel_message` and
 * `suppress_number` since ops #29, `forget_number` with ops #27, `release_number` since ops #33, when
 * the office confirms a number the phone system holds, and `unsuppress_number` since ops #44 step
 * three, when an owner or a manager lifts a stop ops pushed and nothing else holds the number. And
 * the sixth, `place_call` (ops #84 phase 6, `faithfully-kept-phone#32`, live on both of the phone
 * system's hosts since 2026-10-24 and answering `422 not_enabled` there), queued when the office
 * presses Call through the business line on a client's page. Each goes only while the Worker's
 * `PHONE_INSTRUCTIONS_ON` names it, staging first and production once staging has seen it answered,
 * and `api/test/wrangler-config.test.ts` holds both lists to this one, `place_call` on neither yet.
 */
export const PHONE_INSTRUCTIONS = values([
  'cancel_message',
  'suppress_number',
  'release_number',
  'forget_number',
  'unsuppress_number',
  'place_call',
]);
export type PhoneInstruction = (typeof PHONE_INSTRUCTIONS)[number];

export const OUTBOX_STATES = values(['pending', 'sent', 'failed', 'dead']);
export type OutboxState = (typeof OUTBOX_STATES)[number];

/** Who pushed a receipt at us. `erd.md` Part 2: a source discriminator and no stored body. */
export const INBOUND_SOURCES = values(['site', 'phone']);
export type InboundSource = (typeof INBOUND_SOURCES)[number];

export const INBOUND_KINDS = values(['lead', 'call_enquiry', 'lead_status_ack']);
export type InboundKind = (typeof INBOUND_KINDS)[number];

export const ERASURE_STATES = values(['received', 'in_progress', 'completed', 'refused']);
export type ErasureState = (typeof ERASURE_STATES)[number];

/**
 * THE STEPS OF A HOUSEHOLD'S FORGET AFTER THE PRESS (ops #82, `docs/plan-household-forget.md` section
 * 6), in the order they run: the press takes the identity in one batch, and these clear the rest of
 * their record from the request row, one step a run, each bounded in size and in tries. `numbers` runs
 * once a number, `files` once every few photos of their homes (ops #74 item 2: their rows blanked and
 * their objects deleted from R2), `stripe` deletes their Customer, and `completion` closes the request.
 * A list and a type, never a CHECK: the words live in `erasure_requests.targets`, which no CHECK reads.
 */
export const HOUSEHOLD_FORGET_STEPS = values([
  'numbers',
  'messages',
  'calls',
  'work',
  'files',
  'asks',
  'stripe',
  'completion',
]);
export type HouseholdForgetStep = (typeof HOUSEHOLD_FORGET_STEPS)[number];

/*
 * THE HOME SEAM (ops #19, migration `0026`, `docs/seams/home.md`). A household's own door is the
 * client system, `faithfully-kept-home`, and what ops holds of it is a membership: the office's
 * decision that a contact on a household may see it there, never a credential (binding rule 5). The
 * seven lists below are CHECKs on `memberships` and `home_requests`, decided in full by `0026`
 * because a word added later is a rebuild of a live table (gotcha 34); the eighth is a list and a
 * type only. Every word is the contract's, spelled as home spells it on the wire.
 */

/**
 * What a membership lets its person do. A `client` pays, changes visits, edits the home and adds a
 * person; a `member` sees and asks, for the homes named (the owner's decision 5). Home stores `member`
 * under a word of its own and sends `member` back up.
 */
export const MEMBERSHIP_ROLES = values(['client', 'member']);
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

/**
 * `invited` when the office has asked, `granted` once she has signed in at home with an address ops
 * holds for her, `revoked` when it ends. Requests are taken only at `granted`, and a row is never
 * `granted` before a sign-in bound it (`ck_memberships_granted`).
 */
export const MEMBERSHIP_STATES = values(['invited', 'granted', 'revoked']);
export type MembershipState = (typeof MEMBERSHIP_STATES)[number];

/**
 * Which address the first sign-in proved, read off the prefix of home's `subject`. Codes go by email
 * alone in this release, because texts are not in the carrier filing; `sms` is here for the day they
 * are, so that day is no rebuild.
 */
export const MEMBERSHIP_BOUND_VIA = values(['email', 'sms']);
export type MembershipBoundVia = (typeof MEMBERSHIP_BOUND_VIA)[number];

/**
 * Why a membership ended. `superseded` is a fresh invitation replacing it, which is how the office
 * changes a role or the homes. `contact_removed`, `forgotten` and `invitation_expired` wait on doors
 * ops does not have yet (removing a contact, the household forget, expiring an invitation) and are
 * here so those doors are no rebuild.
 */
export const MEMBERSHIP_REVOKE_REASONS = values([
  'contact_removed',
  'household_ended',
  'forgotten',
  'office_decision',
  'superseded',
  'invitation_expired',
]);
export type MembershipRevokeReason = (typeof MEMBERSHIP_REVOKE_REASONS)[number];

/**
 * HOW A DOOR WAS OFFERED, where not by the office's own invitation from the household screen
 * (`memberships.invited_with`, migration `0042`; `docs/plan-clients-only-home.md` section 3).
 * `invoice_email`: "Send the invoice" invited the money contact quietly, in the batch that sends her
 * first invoice email, and home sends no invitation email of its own for it, so the invoice email is
 * her way in. Null on every invitation the office makes by hand, as on every row before `0042`. A
 * CHECK decided in full now, because a word added later is a rebuild (gotcha 34).
 */
export const MEMBERSHIP_INVITED_WITH = values(['invoice_email']);
export type MembershipInvitedWith = (typeof MEMBERSHIP_INVITED_WITH)[number];

/**
 * Every ask home sends up, twenty-nine since `0037`, in the contract's order, sixteen of them taken
 * by the door today. Seven are the office's to decide
 * (`visit.skip`, `visit.move`, `visit.addon`, `visit.instruction`, `home.edit`, `person.add`,
 * `message`), seven are applied on receipt: five because each is what her invoice link already does
 * with nobody behind it (`visit.feedback`, `invoice.feedback`, `invoice.pay`, `invoice.pay_by_zelle`,
 * `billing.autopay_stop`), `invoice.document`, a ten-minute link to one of an invoice's PDFs
 * (ops #76 O5, migration `0035`, after the fourteen because the CHECK took it then), and
 * `invoice.zelle_sent`. `membership.accepted` and `journal.photos` are answered at once.
 * `invoice.zelle_sent`, her report that an invoice's money is on its way by Zelle (ops #71 item 5,
 * `docs/seams/home.md` section 11.7, migration `0036`), is after the fifteen because the CHECK took
 * it then, and is applied on receipt through `reportZelleSent` (`api/src/money/zelle-report.ts`). An
 * unknown type is refused at the door and never stored, so this is the whole of what a row can hold.
 *
 * THIRTEEN MORE SINCE MIGRATION `0037`, after the sixteen, taken into the CHECK in one rebuild so
 * none of them rebuilds the table again (ops #76, `docs/seams/home.md` section 12): the nine asks of
 * sections 12.2 to 12.4, `billing.autopay_start` applied on receipt and eight the office decides, the
 * four schedule asks (`schedule.pause`, `schedule.resume`, `schedule.change`, `service.end`) and the
 * four about people (`person.contact_change`, `person.remove`, `person.change`, `person.leave`); then
 * the four of phases 2 and 3 that section 11.3 names. The door refuses each as it refuses a type it
 * never heard of until its build lands (`TYPES_NOT_YET_APPLIED`, `api/src/seams/home-requests.ts`),
 * so a word here is not yet a word ops takes.
 *
 * ONE MORE SINCE MIGRATION `0040`, last: `invoice.zelle_withdrawn`, her fact that she did not send an
 * invoice's money by Zelle after all, which ends her own standing `invoice.zelle_sent` report
 * (ops #71, `docs/seams/home.md` section 11.7, "Taking it back"). Thirty kinds in all. It waits in
 * `TYPES_NOT_YET_APPLIED` until the change that builds it takes it out.
 */
export const HOME_REQUEST_KINDS = values([
  'membership.accepted',
  'visit.skip',
  'visit.move',
  'visit.addon',
  'visit.instruction',
  'visit.feedback',
  'invoice.feedback',
  'invoice.pay',
  'invoice.pay_by_zelle',
  'billing.autopay_stop',
  'home.edit',
  'person.add',
  'message',
  'journal.photos',
  'invoice.document',
  'invoice.zelle_sent',
  'billing.autopay_start',
  'schedule.pause',
  'schedule.resume',
  'schedule.change',
  'service.end',
  'person.contact_change',
  'person.remove',
  'person.change',
  'person.leave',
  'preferences.set',
  'texts.stop',
  'texts.start',
  'preferences.time_zone',
  'invoice.zelle_withdrawn',
]);
export type HomeRequestKind = (typeof HOME_REQUEST_KINDS)[number];

/** Where a request stands. Anything but `received` is decided, and says when (`ck_home_requests_decided`). */
export const HOME_REQUEST_STATES = values(['received', 'accepted', 'declined', 'cancelled']);
export type HomeRequestState = (typeof HOME_REQUEST_STATES)[number];

/**
 * Why nobody can decide a request any more, each ending in a `request` push so her page never shows
 * an ask that is quietly dead: her membership revoked, the household ended, the visit no longer open,
 * or withdrawn; and since migration `0037`, `schedule_ended`, a schedule ask whose agreement the
 * office ended, or whose home was removed, before it was decided (ops #76, `docs/seams/home.md`
 * section 12.4).
 */
export const HOME_REQUEST_CANCEL_REASONS = values([
  'membership_revoked',
  'household_ended',
  'visit_no_longer_open',
  'withdrawn',
  'schedule_ended',
]);
export type HomeRequestCancelReason = (typeof HOME_REQUEST_CANCEL_REASONS)[number];

/**
 * THE TWELVE KINDS OPS PUSHES DOWN to home, each a snapshot of one record, in the contract's order. A
 * list and a type, never a CHECK: the word lives in the payload of a `home_push` outbox row, as a
 * phone instruction's does, so the eleventh, `agreement` (ops #76, `docs/seams/home.md` section
 * 12.1), cost no rebuild, nor the twelfth, `preferences`, one person's choices about messages (ops #76
 * phase 2, section 11.3), and home stores a kind it does not know rather than refusing it.
 */
export const HOME_PUSH_KINDS = values([
  'membership',
  'household',
  'card',
  'home',
  'visit',
  'request',
  'journal',
  'invoice',
  'message',
  'forget',
  'agreement',
  'preferences',
]);
export type HomePushKind = (typeof HOME_PUSH_KINDS)[number];

/**
 * THE KINDS OF ROW ON A HOUSEHOLD'S TIMELINE (ops #84, `docs/plan-client-workplace.md` section 3.2),
 * in the order of that table: one merged read over the rows that are the record, newest first. A list
 * and a type, never a CHECK: nothing stores the word, the timeline is a read and never a table (the
 * plan's section 11), and the screen draws its filter chips from this same list.
 */
export const TIMELINE_KINDS = values([
  'visit',
  'change',
  // A cleaner who could not come (ops #85), internal, beside the change that carried the choice.
  'call_off',
  'text_in',
  'text_out',
  'email_out',
  'call',
  'ask',
  'invoice',
  'payment',
  'feedback',
  'consent',
  'mark',
  'door',
  'autopay',
  'issue',
  'task',
  'office',
  // A choice about messages or a hold of the office's (ops #76 phase 2, `docs/plan-preferences.md`
  // section 8): shown in the client view, the office's reason left out of it.
  'preference',
]);
export type TimelineKind = (typeof TIMELINE_KINDS)[number];

/**
 * WHOSE EYES THE TIMELINE IS READ WITH: the office's, every row, or the client's, the rows they saw or
 * did and nothing internal, decided on the server by the kind and the state (section 3.2).
 */
export const TIMELINE_VIEWS = values(['office', 'client']);
export type TimelineView = (typeof TIMELINE_VIEWS)[number];

/* ------------------------------------------------------------------ *
 * B. Demand and outbound
 * ------------------------------------------------------------------ */

/**
 * VERBATIM from `../faithfully-kept-business/data/targets.md`, hyphens included, because that file
 * has rows in it today and the migration that replaces it must not rename anybody's status.
 * `gated` is the valuable one: it is what a purchase would unlock, named as lost work.
 */
export const PROSPECT_STATES = values([
  'new',
  'touched',
  'followed-up',
  'replied',
  'quoted',
  'booked',
  'no-reply',
  'declined',
  'gated',
]);
export type ProspectState = (typeof PROSPECT_STATES)[number];

/** The sections of `targets.md`, which are the audiences, not job titles. */
export const PROSPECT_SEGMENTS = values([
  'realtor',
  'property_manager',
  'community_group',
  'short_term_rental',
  'resident',
  'other',
]);
export type ProspectSegment = (typeof PROSPECT_SEGMENTS)[number];

/**
 * One vocabulary for "how did this happen or arrive", used by enquiries, clients, prospects,
 * touches, expenses and campaigns, so priority 7 (say where clients came from) is answerable with
 * one GROUP BY rather than six.
 *
 * TWENTY-ONE WORDS, AND EVERY ONE OF THEM IS SOMETHING THE MONEY IS COUNTED IN. `first-20-clients.md`
 * budgets its sources and gives five of them a quantified kill rule, so a channel that shares a
 * word with another channel is a kill rule counted against the wrong spend. That is the whole rule
 * this list is built on, and it was applied three times: once when `google` was split into
 * `thumbtack`, `local_services` and `search_ads`, again in migration `0006`, which rebuilt the five
 * tables carrying this CHECK to add the six below, and in migration `0051` (ops #90), which rebuilt
 * the six that carry it now to add `chatgpt_ads`.
 *
 * A kill rule counted against the wrong channel is a kill rule that never fires.
 */
export const CHANNELS = values([
  'website_form',
  // A visitor who read the number on the page and rang it. The site observes the press and pushes
  // it (`docs/seams/website.md` section 8); ops cannot observe it at all, because the business
  // publishes one number and means to keep publishing one. It is a REMAINDER and never a total:
  // where the site knows the session came from a paid channel it sends that word instead, so this
  // collects the calls no paid or platform source produced. It is typeable, unlike `website_form`,
  // because "I found your number on your website" is an answer somebody hears on the phone every
  // week and the desktop half of that touchpoint is unobservable by design.
  'website_call',
  'phone',
  'sms',
  'email',
  'referral',
  // Both free and both budgeted at nothing, and both worth separating from the words above so a
  // household somebody already knew is not counted as a channel that could be bought again.
  'personal_network',
  'realtor',
  // Free replies in a community group, and the paid flight beside them. One booking from a free
  // Lansbrook thread would otherwise suppress a paid flight's kill rule for ever.
  'nextdoor',
  'nextdoor_ads',
  'facebook',
  // $150 to $400 per recurring household, and "$300 spent with zero bookings" ends it. No
  // `instagram`: the money is one flight bought in one place, so an Instagram lead is this word
  // with `instagram` in `source_detail`.
  'facebook_ads',
  'google',
  // The two paid Google channels, named separately from `google` because each carries a quantified
  // kill rule in `first-20-clients.md` (ten Local Services leads with no booking pauses it, fifteen
  // Thumbtack leads with no booking ends it).
  'thumbtack',
  'local_services',
  'search_ads',
  // The third paid ad platform beside Google and Meta (ops #90, the owner through business #41,
  // 2026-10-23): ads in ChatGPT, bought from OpenAI by growth and tagged `utm_source=chatgpt`, which
  // the website maps to this word. A $150 a month test cap outside the plan's totals, and "$150
  // spent with zero leads, or 30 days, whichever comes first" ends it; no cost per household is
  // budgeted, because none is published.
  'chatgpt_ads',
  // $50 to $150 a household. Its rule is judged by a person in `data/jobs.md`; the word exists so
  // that its $300 lands on no other channel's cost.
  'oven_hook',
  'letter',
  'in_person',
  'other',
]);
export type Channel = (typeof CHANNELS)[number];

/**
 * What a person may type into a source field: everything above except `website_form`.
 *
 * A form lead has a `lead_id` the website minted and nothing else does, so `createEnquiry` refuses
 * that one word and `updateEnquiry` refuses it as a correction. It is ONE exclusion and not two:
 * `website_call` is in this list on purpose, for the reason its own comment gives.
 *
 * It lives here rather than in the screen that first needed it, because three surfaces read it now
 * (the enquiries screen, the new-client form and the client patch) and a list filtered in three
 * places is a list that disagrees with itself.
 */
export const TYPED_CHANNELS = values(
  CHANNELS.filter((channel) => channel !== 'website_form') as Exclude<Channel, 'website_form'>[],
);
export type TypedChannel = (typeof TYPED_CHANNELS)[number];

export const TOUCH_DIRECTIONS = values(['outbound', 'inbound']);
export type TouchDirection = (typeof TOUCH_DIRECTIONS)[number];

/**
 * VERBATIM from `../faithfully-kept-business/data/outreach-log.md`. `none` is the row that makes a
 * reply rate honest: `erd.md` records touches including the sends that got nothing, because a rate
 * needs its denominator stored.
 */
export const TOUCH_OUTCOMES = values([
  'sent',
  'replied',
  'quoted',
  'booked',
  'declined',
  'gated',
  'none',
]);
export type TouchOutcome = (typeof TOUCH_OUTCOMES)[number];

/**
 * OPS-SIDE NAMES, and the one vocabulary in this file with an open question against it. The site
 * owns the lead and its five-state callback contract; `architecture.md` section 3 says ops reports
 * back through `POST /api/lead/status`. The wire vocabulary lives in the website repository, which
 * is out of scope and whose local clone is stale, so these are the ops names and the mapping to
 * the wire is confirmed with the website's owner before the seam is built in step 7. Nothing in
 * waves one or two depends on the mapping.
 */
export const ENQUIRY_STATES = values(['new', 'contacted', 'quoted', 'booked', 'lost']);
export type EnquiryState = (typeof ENQUIRY_STATES)[number];

/**
 * WHAT HELD A QUOTE BACK (ops #86, `docs/plan-coach-review.md` section 5), the coach review's note on
 * every unbooked enquiry: one word the office types on a quoted enquiry as it learns it, usually on
 * the day-one call, behind `ck_enquiries_held_back` (migration `0048`), with the person's own words
 * beside it in `enquiries.held_back_note`. Seven, confirmed by the business on 2026-10-22, and a
 * count of them per source is what tells "price" from "no answer" (report A's channel page). Apart
 * from `lost_reason`, which stays the words a lost enquiry is lost for: a quote still open is not lost.
 */
export const HELD_BACK_REASONS = values([
  'price',
  'timing',
  'thinking_it_over',
  'comparing_quotes',
  'scope',
  'no_answer',
  'other',
]);
export type HeldBackReason = (typeof HELD_BACK_REASONS)[number];

/**
 * THE TWO WAYS A PERSON FOLLOWS A QUOTE UP (ops #86): a call and an email, each a `touches.channel`
 * word (`CHANNELS`). Never a text: the composer refuses an enquiry by text, and so does this list.
 */
export const FOLLOW_UP_CHANNELS = values(['phone', 'email']);
export type FollowUpChannel = (typeof FOLLOW_UP_CHANNELS)[number];

/**
 * WHY SOMETHING WAS THROWN AWAY, and the one list here with NO column behind it and no CHECK.
 *
 * A test lead, a test call and a junk enquiry are the three rows this system had no way to remove,
 * and they are all the same act: somebody decides a row was never a person. The reason is asked
 * for because "it was a test" and "it was spam" are different facts about the business, and it is
 * NOT stored in a column of its own, because a column invites a report, a report invites a rate,
 * and nobody is going to be measured on how much rubbish arrived. It is written into the row's own
 * notes in the same audited batch as the delete, where the whole prior row is already kept, so a
 * restore puts back the record and the sentence that explains it together.
 *
 * `privacy_request` IS THE ONE THAT IS NOT A DISCARD AT ALL, added 2026-09-09 with the website's
 * forget contract. The other four say the row was never a person. This one says the row IS a
 * person and she has asked to stop being one here, so the enquiry is blanked before it is soft
 * deleted, every prior audit image of it is replaced by a redaction image, and the website is told
 * `forget` rather than `discarded`. It sits in this list rather than in one of its own because it
 * arrives through the same button on the same screen, and a second list would be a second place
 * for the two to disagree about which words exist.
 */
export const DISCARD_REASONS = values(['test', 'spam', 'duplicate', 'privacy_request', 'other']);
export type DiscardReason = (typeof DISCARD_REASONS)[number];

/**
 * THE FOUR A DOOR THAT ERASES NOTHING IS ALLOWED TO TAKE, and it exists because the word above it
 * is a promise rather than a label.
 *
 * `privacy_request` means the row is blanked, soft deleted and its every audit image replaced. One
 * door in this system does that: the enquiry. The call log, the expense book and the task list
 * share the same button and the same shape, and all three would have answered 200 to the word,
 * written "Discarded: a privacy request." into the row's own free text, kept her name and her
 * number exactly where they were, and written two fresh before-images holding the whole prior row.
 * The plan was for the screens to leave the option out of those three sheets, which makes the
 * interface the control for a promise the server made: CLAUDE.md binding rule 6 says that hiding
 * something in the UI is UX and never the control. So the server refuses the word it cannot
 * honour, and the refusal is the same answer whether or not any screen ever filters it.
 */
export const NON_ERASING_DISCARD_REASONS = values(['test', 'spam', 'duplicate', 'other']);
export type NonErasingDiscardReason = (typeof NON_ERASING_DISCARD_REASONS)[number];

/**
 * WHAT A FORGET DID ABOUT THE PHONE SYSTEM'S COPIES OF HER TEXTS (ops #27), as the office's privacy
 * button is answered and says afterwards: `asked`, the phone system asked to erase everything at each
 * number she used; `held_elsewhere`, not asked about a number a client contact or another enquiry
 * still holds, because its erasure is of everything at the number, theirs included; `no_number`,
 * nothing to ask about. The sheet cannot know which before the press, so the answer says it. No
 * column holds it, so it is a list and not a CHECK.
 */
export const PHONE_FORGET_OUTCOMES = values(['asked', 'held_elsewhere', 'no_number']);
export type PhoneForgetOutcome = (typeof PHONE_FORGET_OUTCOMES)[number];

export const QUOTE_STATES = values(['draft', 'given', 'accepted', 'declined', 'expired']);
export type QuoteState = (typeof QUOTE_STATES)[number];

/**
 * Who asserted that the home had not been professionally cleaned in ninety days, which decides
 * whether the first visit is priced as a deep clean. The business brief is explicit that this is
 * said before booking and never on the invoice afterwards, so the assertion needs an author.
 *
 * The OWNER SETTLED on 2026-09-03 that the ninety-day rule is a fact about the HOUSE and not about
 * the client, so the clock itself lives on `sites.last_professional_clean_on` and this vocabulary
 * records how we came to believe it. `we_cleaned_it` is the only one that is not somebody's word.
 */
export const DEEP_CLEAN_BASIS = values([
  'client_said',
  'owner_assessed',
  'we_cleaned_it',
  'recent_clean_known',
  'not_applicable',
]);
export type DeepCleanBasis = (typeof DEEP_CLEAN_BASIS)[number];

/** The ninety days the deep-clean rule turns on, published on the pricing page. */
export const DEEP_CLEAN_AFTER_DAYS = 90;

/**
 * How this client is told a cleaner is on the way this morning.
 *
 * The OWNER SETTLED on 2026-09-03 that all three paths exist and the operator picks: automatic for
 * a client who wants it, drafted and approved for one she would rather write to herself, and off
 * for somebody who asked not to be messaged. This is the default per client; the move flow can
 * still override it on the day, because a visit moving is a different message from a visit
 * arriving.
 *
 * There is no fourth value meaning "marketing". Messages here are operational only.
 */
export const NOTICE_MODES = values(['automatic', 'ask_first', 'never']);
export type NoticeMode = (typeof NOTICE_MODES)[number];

/**
 * THE LANGUAGE THE HOUSEHOLD READS (ops #98, business #49, `docs/plan-client-language.md` section 3),
 * on `clients.language` since migration `0053`: English for every household until the office sets
 * Spanish on the client's word. The business named two languages and no third, and a third is a
 * widening of `ck_clients_language`, which is a rebuild of `clients` (`docs/gotchas.md` entry 34).
 * Pushed to home on the `household` kind, so home knows which too. Ops never guesses what language a
 * text is in, and no machine translates anything.
 */
export const CLIENT_LANGUAGES = values(['en', 'es']);
export type ClientLanguage = (typeof CLIENT_LANGUAGES)[number];

export const EXPENSE_CATEGORIES = values([
  'insurance',
  'bond',
  'supplies',
  'equipment',
  'marketing',
  'software',
  'phone',
  'vehicle',
  'state_fees',
  'professional',
  'cleaner_pay',
  'other',
]);
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

/** About $107 falls due after the referred client's second visit, and nobody will chase it. */
export const REFERRAL_STATES = values(['pending', 'due', 'paid', 'void']);
export type ReferralState = (typeof REFERRAL_STATES)[number];

/**
 * Where a campaign stands, as the office says it. A campaign that never ran is thrown away with a
 * reason rather than given a state, and a campaign is a cost, a date range and an attribution,
 * never a thing that sends, so there is no `scheduled` and no `sending`.
 *
 * `paused` is the fourth since migration `0028` (ops #49, the owner, 2026-10-15): a flight switched
 * off for a while, which growth sends while it rests and turns back to `running` when it resumes.
 * It is a word somebody says about the campaign and never what decides the counting: a paused
 * campaign's spend and leads still belong to its days, exactly as a running one's do.
 */
export const CAMPAIGN_STATUSES = values(['planned', 'running', 'paused', 'ended']);
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

/* ------------------------------------------------------------------ *
 * C. The client and the home
 * ------------------------------------------------------------------ */

export const CLIENT_STATUSES = values(['active', 'paused', 'ended']);
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

export const CONTACT_ROLES = values([
  'primary',
  'second_household',
  'billing',
  'emergency',
  'property_manager',
]);
export type ContactRole = (typeof CONTACT_ROLES)[number];

/**
 * `preferred_channel` is on `contacts` because "call, do not text" decides whether the
 * told-before-the-move promise is experienced as kept.
 */
export const PREFERRED_CHANNELS = values(['call', 'sms', 'email']);
export type PreferredChannel = (typeof PREFERRED_CHANNELS)[number];

/** Two counties, not one: every target ZIP is Pinellas except 34655, which is Pasco. */
export const COUNTIES = values(['pinellas', 'pasco']);
export type County = (typeof COUNTIES)[number];

/**
 * Four of the six target communities are gated, and the site publishes that vendor registration is
 * arranged before the first visit, so the state of that arrangement is a column and not a note.
 */
export const VENDOR_REGISTRATION_STATES = values([
  'not_required',
  'required_not_started',
  'submitted',
  'approved',
  'refused',
  'expired',
]);
export type VendorRegistrationState = (typeof VENDOR_REGISTRATION_STATES)[number];

/**
 * Every kind the table may HOLD, and so every kind a read may meet: the CHECK constraint's list.
 * What may be WRITTEN is `WRITABLE_ACCESS_SECRET_KINDS` below, since a kind can be retired without
 * the rebuild a narrowed CHECK costs on D1 (`docs/gotchas.md` entry 34).
 *
 * `alarm_code` was the reason this list exists, and it is retired (the owner, 2026-10-24, business
 * #47): the alarm is the household's, and the business never holds its code. A row kept from
 * before still reads, to the office only, marked retired, and is removed on the record. It stays
 * `owner_only` by `CHECK (kind <> 'alarm_code' OR sensitivity = 'owner_only')` in the first
 * migration, and the cleaner's readers leave the kind out on their own besides.
 */
export const ACCESS_SECRET_KINDS = values([
  'door_code',
  'lockbox_code',
  'gate_code',
  'garage_code',
  'alarm_code',
  'key_location',
  'wifi',
  'other',
]);
export type AccessSecretKind = (typeof ACCESS_SECRET_KINDS)[number];

/** Kinds the table still holds and nothing writes again: refused at the door, never given a cleaner. */
export const RETIRED_ACCESS_SECRET_KINDS = values(['alarm_code'] as const);
export type RetiredAccessSecretKind = (typeof RETIRED_ACCESS_SECRET_KINDS)[number];

/** Why each retired kind is no longer kept, the one sentence the door refuses with and the list shows. */
export const RETIRED_ACCESS_SECRET_KIND_REASONS: Record<RetiredAccessSecretKind, string> =
  Object.freeze({
    alarm_code:
      "The alarm is the household's: they turn it off before the visit and on after, and the business never holds its code, so an alarm code is not kept here.",
  });

export function isRetiredAccessSecretKind(kind: string): kind is RetiredAccessSecretKind {
  return (RETIRED_ACCESS_SECRET_KINDS as readonly string[]).includes(kind);
}

/** What a new code may be: every kind the table holds but a retired one, in the same order. */
export const WRITABLE_ACCESS_SECRET_KINDS = values([
  'door_code',
  'lockbox_code',
  'gate_code',
  'garage_code',
  'key_location',
  'wifi',
  'other',
] as const satisfies readonly Exclude<AccessSecretKind, RetiredAccessSecretKind>[]);
export type WritableAccessSecretKind = (typeof WRITABLE_ACCESS_SECRET_KINDS)[number];

export const SENSITIVITIES = values(['normal', 'owner_only']);
export type Sensitivity = (typeof SENSITIVITIES)[number];

export const KEY_KINDS = values(['house_key', 'gate_fob', 'garage_remote', 'mail_key', 'lockbox']);
export type KeyKind = (typeof KEY_KINDS)[number];

/**
 * Append-only, and what makes offboarding eight phone calls rather than a memory exercise.
 * `lost` is in the list because the row that matters most is the one nobody wants to write.
 */
export const KEY_CUSTODY_EVENTS = values([
  'received_from_client',
  'handed_to_cleaner',
  'returned_by_cleaner',
  'returned_to_client',
  'lost',
  'destroyed',
]);
export type KeyCustodyEvent = (typeof KEY_CUSTODY_EVENTS)[number];

/**
 * WHERE A HOME PLAN'S WORDS CAME FROM (ops #74 item 6, migration `0056`,
 * `docs/plan-cleaner-app-backend.md` decision 16): the household's own words about their home, its
 * rooms, what matters in it and what they wish for, approved by the office and frozen onto the sheet.
 * Typed by the office from a call or from a walkthrough of the home, or one day sent up from home by
 * the household itself, `client_via_home`, which waits on home's seam: decided in full at creation, so
 * the third word costs no rebuild of the table later (gotcha 34), and written by nothing in this build.
 */
export const HOME_PLAN_SOURCES = values([
  'client_via_home',
  'office_from_call',
  'office_from_walkthrough',
]);
export type HomePlanSource = (typeof HOME_PLAN_SOURCES)[number];

/** What the office's door writes: every word but the one home's seam will bring. */
export const OFFICE_HOME_PLAN_SOURCES = values([
  'office_from_call',
  'office_from_walkthrough',
] as const satisfies readonly Exclude<HomePlanSource, 'client_via_home'>[]);
export type OfficeHomePlanSource = (typeof OFFICE_HOME_PLAN_SOURCES)[number];

/** The longest a home plan may be, held at the door and never in a CHECK, as the do-not list is. */
export const HOME_PLAN_BODY_MAX = 4000;

/* ------------------------------------------------------------------ *
 * D. What was agreed, and the schedule
 * ------------------------------------------------------------------ */

/**
 * The published card sells One time, Every 4 weeks, Every 2 weeks and Weekly. It never sells
 * "monthly", which is what removes month-end clamping from the materialiser entirely.
 */
export const CADENCES = values(['one_time', 'every_4_weeks', 'every_2_weeks', 'weekly']);
export type Cadence = (typeof CADENCES)[number];

/** Days between visits for each recurring cadence. `one_time` has no interval, hence null. */
export const CADENCE_INTERVAL_DAYS: Record<Cadence, number | null> = Object.freeze({
  one_time: null,
  every_4_weeks: 28,
  every_2_weeks: 14,
  weekly: 7,
});

/** `erd.md` C2: a weekday carries no phase, so a series anchors on a date plus an interval. */
export const INTERVAL_DAYS = values(['7', '14', '28']);

export const SERVICE_LINES = values(['house_cleaning', 'home_watch', 'turnover']);
export type ServiceLine = (typeof SERVICE_LINES)[number];

export const AGREEMENT_STATES = values(['draft', 'active', 'paused', 'ended']);
export type AgreementState = (typeof AGREEMENT_STATES)[number];

/**
 * `erd.md` C4: `superseded_cadence_change` must be separable from real churn, or every cadence
 * upgrade is counted as a lost client and the retention number lies in the flattering direction.
 */
export const ENDED_REASONS = values([
  'superseded_cadence_change',
  'client_ended',
  'we_ended',
  'moved_away',
  'season_ended',
  'non_payment',
  'never_started',
]);
export type EndedReason = (typeof ENDED_REASONS)[number];

/**
 * Where a price came from, and the whole point of `erd.md` C3: without this the nightly check
 * cries wolf on the first card change and on every sanctioned zero-dollar visit, and a check that
 * cries wolf is a check that gets muted.
 *
 * Every value except `card` is a published rule that produces a figure differing from the grid.
 * `late_cancellation_half_rate` is the one carrying an upstream defect: the cancellation policy
 * page publishes the half rate and the pricing page publishes no such line and says there is no
 * cancellation fee. See `erd.md` Part 9. It is sanctioned here so the breach list stays readable,
 * and named there so the contradiction stays visible.
 */
export const PRICE_SOURCES = values([
  'card',
  'card_deep_clean',
  'card_move_out',
  'card_addon',
  'quoted_walkthrough',
  'sanctioned_zero',
  'reclean_no_charge',
  'missed_visit_no_charge',
  'late_cancellation_half_rate',
]);
export type PriceSource = (typeof PRICE_SOURCES)[number];

export const PAYMENT_AUTH_STATES = values(['active', 'revoked', 'expired']);
export type PaymentAuthState = (typeof PAYMENT_AUTH_STATES)[number];

/**
 * A SAVED METHOD IS HERS TO STOP (ops #46, step 3, migration `0020`). She ticks Stripe's own save box
 * on a page ops made, and `payment_authorizations` records what she saved, where, and later who
 * stopped it and through which door. Each of the four lists below is a CHECK on that table, and
 * widening one later is a rebuild (gotcha 34), so each is decided in full now.
 *
 * Who holds the method. `square` is the processor the table was first written for and holds no row;
 * `stripe` since roadmap B. Kept because a word nobody uses costs nothing and a word added later is a
 * rebuild.
 */
export const PROCESSORS = values(['square', 'stripe']);
export type Processor = (typeof PROCESSORS)[number];

/**
 * What she saved, in Stripe's own words for a PaymentMethod's `type`, and only the three a page of
 * ours offers (`PAGE_METHODS` in `api/src/money/stripe.ts`): a card, which is also how Apple Pay and
 * Google Pay arrive, a US bank account, and Link. Never a pay-later method, which no page offers.
 */
export const SAVED_METHOD_TYPES = values(['card', 'us_bank_account', 'link']);
export type SavedMethodType = (typeof SAVED_METHOD_TYPES)[number];

/**
 * The wallet a saved card came through, in Stripe's words for `card.wallet.type`, and only the three
 * a page of ours can bring: Apple Pay and Google Pay ride the card, and Link can pay with a card.
 * Null for a card typed in, and for every other wallet, which no page offers.
 */
export const SAVED_METHOD_WALLETS = values(['apple_pay', 'google_pay', 'link']);
export type SavedMethodWallet = (typeof SAVED_METHOD_WALLETS)[number];

/**
 * Which door stopped it. `office` is a signed-in person, and the row names who; `home` is the
 * client's own door of roadmap D, carried now and written by nothing yet; `stripe` is Stripe saying
 * the method is gone, a detach or a mandate gone inactive; `system` is ops itself, the forget when it
 * is built. Its own list rather than `DO_NOT_CONTACT_DOORS`, which has no `stripe` and could only
 * gain one by a rebuild.
 */
export const AUTHORIZATION_DOORS = values(['office', 'home', 'stripe', 'system']);
export type AuthorizationDoor = (typeof AUTHORIZATION_DOORS)[number];

/**
 * Why it was stopped. CODE ONLY AND NOT A CHECK: `revoke_reason` has existed since the first
 * migration, and a CHECK on an existing column is a rebuild, so this list governs every write in code
 * instead. `method_gone` is Stripe's door (detached, mandate inactive); `forgotten` is the forget's,
 * when the client forget is built; `charge_declined` is the office choosing to stop after a decline;
 * the other three are the office's for the reason given.
 */
export const AUTHORIZATION_REVOKE_REASONS = values([
  'client_request',
  'card_replaced',
  'method_gone',
  'charge_declined',
  'forgotten',
  'owner_decision',
]);
export type AuthorizationRevokeReason = (typeof AUTHORIZATION_REVOKE_REASONS)[number];

/**
 * The reasons a person at the office may give for stopping it: every one above except `forgotten`,
 * which only the forget writes. A stop typed at the office under that word would claim a forget that
 * never happened.
 */
export const OFFICE_REVOKE_REASONS = values(
  AUTHORIZATION_REVOKE_REASONS.filter((reason) => reason !== 'forgotten') as Exclude<
    AuthorizationRevokeReason,
    'forgotten'
  >[],
);
export type OfficeRevokeReason = (typeof OFFICE_REVOKE_REASONS)[number];

export const CLOSED_DATE_KINDS = values(['holiday', 'owner_unavailable', 'storm', 'other']);
export type ClosedDateKind = (typeof CLOSED_DATE_KINDS)[number];

/* ------------------------------------------------------------------ *
 * E. The visit
 * ------------------------------------------------------------------ */

/**
 * SIX VALUES, AND THIS IS A PERMANENT CHECK CONSTRAINT. `erd.md` Part 2 settled it and Part 7
 * item 9 restates it: adding a foreign key to `visits` later on D1 is a twelve-step rebuild of the
 * largest table in the system while it is live. A re-clean is not a kind: it is an ordinary visit
 * carrying `is_reclean_for_visit_id` and a `reclean_no_charge` price source.
 */
export const VISIT_KINDS = values([
  'recurring',
  'one_time',
  'deep_clean',
  'move_out',
  'home_watch',
  'turnover',
]);
export type VisitKind = (typeof VISIT_KINDS)[number];

export const VISIT_STATUSES = values([
  'planned',
  'confirmed',
  'in_progress',
  'done',
  'cancelled',
  'no_access',
]);
export type VisitStatus = (typeof VISIT_STATUSES)[number];

/**
 * `erd.md` C6. `origin` plus `planned_date` plus the unique index is what stops the nightly
 * materialiser reverting a move the client was already told about, which is the single worst
 * failure this system can have.
 */
export const VISIT_ORIGINS = values(['materialised', 'manual', 'moved', 'access_retry', 'reclean']);
export type VisitOrigin = (typeof VISIT_ORIGINS)[number];

export const VISIT_CHANGE_KINDS = values([
  'moved',
  'cancelled_by_client',
  'cancelled_by_us',
  'paused',
  'no_access_rescheduled',
  'window_changed',
  'cleaner_changed',
]);
export type VisitChangeKind = (typeof VISIT_CHANGE_KINDS)[number];

/**
 * A visit does not move until the client has been told, or one of these says in words why not.
 * A change row with neither `told_at` nor a `silence_reason` is a defect on the schedule, and the
 * nightly check reports it as one.
 */
export const SILENCE_REASONS = values([
  'told_by_phone',
  'told_in_person',
  'client_requested_the_change',
  'no_contact_details_yet',
  'system_correction',
]);
export type SilenceReason = (typeof SILENCE_REASONS)[number];

/**
 * The published policy: more than 24 hours notice is no charge without limit; under 24 hours is
 * still no charge the first time, and after that we may charge half the visit rate, told before
 * rather than after. `not_decided` exists because the decision is a human one and the row is
 * written when the change is, not when somebody makes up their mind.
 */
export const CHARGE_DECISIONS = values(['not_decided', 'no_charge', 'half_rate', 'full_rate']);
export type ChargeDecision = (typeof CHARGE_DECISIONS)[number];

/**
 * A CLEANER WHO CANNOT COME (ops #85, business #35, migration `0046`): the one word the cleaner gave
 * the office, as the business named the four. A CHECK on `visit_call_offs.reason`, so a fifth word
 * later is a rebuild of that small table and never of `visit_changes` (`docs/plan-call-off.md`
 * section 3). The word reaches the office alone: never a client, a co-worker or home.
 */
export const CALL_OFF_REASONS = values(['illness', 'family', 'car', 'other']);
export type CallOffReason = (typeof CALL_OFF_REASONS)[number];

/**
 * How the office heard. `app` is the cleaner app's own button, written by nothing until the app builds
 * its door (`docs/seams/cleaner-app.md`), carried now because a word added later is a rebuild.
 */
export const CALL_OFF_REPORTED_VIA = values(['phone', 'text', 'in_person', 'app']);
export type CallOffReportedVia = (typeof CALL_OFF_REPORTED_VIA)[number];

/**
 * What became of a call-off. The first three are the client's choice, each carried by the change that
 * told them (`schedule/changes.ts`): a new slot, one named substitute, or the visit skipped at no
 * charge. `visit_cancelled` is a visit taken off by another path, a pause, an ending, a cadence change
 * or a move to another weekday (`cancelVisits`), which closes the call-off with the change it wrote.
 * Null while the office has not yet recorded the choice.
 */
export const CALL_OFF_CHOICES = values(['moved', 'substitute', 'skipped', 'visit_cancelled']);
export type CallOffChoice = (typeof CALL_OFF_CHOICES)[number];

/**
 * The days a cleaner's call-offs are counted back over, from today: the business's own line, three in
 * sixty days, for a conversation the office opens (`call-off-plan.md` section 5). A count read for one
 * cleaner, never stored, never ranked, never ordered by, and nothing automatic behind it.
 */
export const CALL_OFF_WINDOW_DAYS = 60;

/**
 * There is no `removed`. Taking a cleaner off a visit is a soft delete like every other removal in
 * this system, which keeps `docs/identity.md`'s scope root exactly as it is written there:
 * `cleaner_id = me AND deleted_at IS NULL`. A second way to express the same fact is a second way
 * for a predicate to be subtly wrong.
 */
export const ASSIGNMENT_STATUSES = values(['assigned', 'accepted', 'declined']);
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];

/** How the client agreed to an add-on. Nothing reaches an invoice that was not agreed first. */
export const AGREED_VIA = values(['call', 'sms', 'email', 'in_person', 'website_form']);
export type AgreedVia = (typeof AGREED_VIA)[number];

export const ISSUE_KINDS = values([
  'damage',
  'breakage',
  'missed_area',
  'complaint',
  'access_problem',
  'safety',
  'other',
]);
export type IssueKind = (typeof ISSUE_KINDS)[number];

export const ISSUE_STATES = values(['open', 'acknowledged', 'remedied', 'closed']);
export type IssueState = (typeof ISSUE_STATES)[number];

/**
 * The published re-clean policy, and the reason `choice_belongs_to` exists: if we cannot get back
 * within a week the client is refunded in full instead, and the choice between the two is hers
 * rather than ours.
 */
export const REMEDIES = values(['reclean', 'refund', 'repair', 'replacement', 'none_agreed']);
export type Remedy = (typeof REMEDIES)[number];

export const CHOICE_OWNERS = values(['client', 'us']);
export type ChoiceOwner = (typeof CHOICE_OWNERS)[number];

/**
 * One polymorphic file store, because three stores would mean three predicates in a deletion
 * procedure that a published thirty-day promise depends on.
 */
export const FILE_OWNER_KINDS = values([
  // A photo of a visit, taken before its report exists (ops #74 item 2, migration `0057`): the visit
  // is there from the materialiser on, and a report has no row until its first save.
  'visit',
  'visit_report',
  'issue',
  'cleaner',
  'client',
  'site',
  'policy',
  'certificate',
  'invoice',
]);
export type FileOwnerKind = (typeof FILE_OWNER_KINDS)[number];

export const FILE_KINDS = values([
  'photo',
  'document',
  'w9',
  'signed_agreement',
  'invoice_pdf',
  'report',
  'certificate',
]);
export type FileKind = (typeof FILE_KINDS)[number];

/**
 * A PHOTO, WITH PERMISSION (ops #74 item 2, `docs/plan-cleaner-app-backend.md` phase 8 and decisions 18
 * to 20): of a visit, or of an issue raised on one, and nothing else. The three containers ops reads by
 * their bytes, each walked against an allowlist of what an image needs, and the limits the contract
 * names: at most 10 MB, twelve on a visit and twelve on an issue, and a caption of at most 140.
 */
export const PHOTO_OWNER_KINDS = values(['visit', 'issue']);
export type PhotoOwnerKind = (typeof PHOTO_OWNER_KINDS)[number];

export const PHOTO_CONTENT_TYPES = values(['image/jpeg', 'image/png', 'image/webp']);
export type PhotoContentType = (typeof PHOTO_CONTENT_TYPES)[number];

export const PHOTO_MOST_BYTES = 10 * 1024 * 1024;
export const PHOTOS_PER_OWNER = 12;
export const PHOTO_CAPTION_MAX = 140;

/* ------------------------------------------------------------------ *
 * F. Money
 * ------------------------------------------------------------------ */

/**
 * `due_on` and `state` exist because priority 5 asks what is owed and what is late without opening
 * a spreadsheet. The published terms are payment within seven days of the invoice, and the invoice
 * comes after the visit, never before it.
 */
export const INVOICE_STATES = values([
  'draft',
  'sent',
  'part_paid',
  'paid',
  'overdue',
  'void',
  'written_off',
]);
export type InvoiceState = (typeof INVOICE_STATES)[number];

export const INVOICE_TERMS_DAYS = 7;

export const PAYMENT_KINDS = values(['payment', 'refund', 'correction']);
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

/** Published: card, bank transfer, check or cash, no surcharge on any of them. Zelle is real. */
export const PAYMENT_METHODS = values(['card', 'bank_transfer', 'zelle', 'check', 'cash', 'other']);
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * What turns the blind Zelle and cheque share into a number instead of a feeling. `stripe` since
 * migration 0010: a payment Stripe reported through its signed webhook, which nobody typed in.
 */
export const ENTRY_SOURCES = values(['square', 'owner_entered', 'bank_statement', 'stripe']);
export type EntrySource = (typeof ENTRY_SOURCES)[number];

/**
 * What a person may say a payment they are typing in came from: everything above except `stripe`.
 * A Stripe payment arrives signed, with the event that reported it, and a row typed in under that
 * word would be a claim no signature made, sitting exactly where the monthly close trusts it most.
 */
export const TYPED_ENTRY_SOURCES = values(
  ENTRY_SOURCES.filter((source) => source !== 'stripe') as Exclude<EntrySource, 'stripe'>[],
);
export type TypedEntrySource = (typeof TYPED_ENTRY_SOURCES)[number];

/**
 * WHICH OF STRIPE'S PAGES AN INVOICE CARRIES, since migration 0018 (ops #46). The pay page is a
 * waterfall, one way to pay per screen, decided by the owner on 2026-09-18: Zelle, which costs the
 * business nothing, then a bank account through Stripe, then a card. `all` is the office's page,
 * every way Stripe takes for this business at once (card with Apple Pay and Google Pay, US bank
 * account and Link); `bank` is the second screen's, a US bank account and nothing else; `card` is the
 * third's, card and Link. A client who asks for one is never handed another, so a page of another
 * kind comes off before hers is made. Every page is at the published price, and none is financing.
 * Decided in full, because widening a CHECK is a rebuild (`docs/gotchas.md` entry 34).
 */
export const PAY_PAGE_KINDS = values(['all', 'bank', 'card']);
export type PayPageKind = (typeof PAY_PAGE_KINDS)[number];

/** The two pages a client's own link may ask for, one screen each, the bank before the card. */
export const LINK_PAY_PAGES = values(['bank', 'card']);
export type LinkPayPage = (typeof LINK_PAY_PAGES)[number];

/**
 * WHO ASKED FOR THE PAGE AN INVOICE CARRIES (`invoices.stripe_checkout_asked_by`, migration `0042`;
 * `docs/plan-clients-only-home.md` section 2): `home`, her press at home (`invoice.pay`); `link`, her
 * invoice's texted link; `office`, the office's button for its page of every way. Written beside the
 * session and cleared with the page. Home's copy of her invoice carries the page only where home asked
 * for it, which the return's origin stops telling once the office's page returns to home as well. A
 * CHECK decided in full now (gotcha 34).
 */
export const PAGE_ASKERS = values(['home', 'link', 'office']);
export type PageAsker = (typeof PAGE_ASKERS)[number];

/**
 * WHERE A PAGE THAT SAVES WITH NOTHING OWED STANDS (ops #76, `billing.autopay_start`, migration `0038`,
 * `docs/seams/home.md` section 12.2): `claimed` from the request's batch until Stripe answers, `ready`
 * with its address, `completed` once she saved on it, and three endings without: `expired` at Stripe,
 * `retired` by ops for a page of the other kind, and `failed`, where no page could be made or the
 * setup will not succeed. Its kinds are `LINK_PAY_PAGES`, the bank and the card. Decided in full,
 * because widening a CHECK is a rebuild (`docs/gotchas.md` entry 34).
 */
export const SETUP_PAGE_STATES = values([
  'claimed',
  'ready',
  'completed',
  'expired',
  'retired',
  'failed',
]);
export type SetupPageState = (typeof SETUP_PAGE_STATES)[number];

/**
 * THE SCREENS OF THE PAY WATERFALL a client's link records she reached, each stamped the first time,
 * which is the funnel the owner asked to see measured: Zelle shown, the help on using Zelle opened,
 * the bank screen shown, the card screen shown. Which of Stripe's pages she then asked for, and the
 * method she finally paid with, are counted beside them. A step added in front of the waterfall, such
 * as a gift card's code, is one more nullable stamp, never a rebuild.
 */
export const PAY_SCREENS = values(['zelle', 'zelle_help', 'bank', 'card']);
export type PayScreen = (typeof PAY_SCREENS)[number];

/**
 * WHERE STRIPE SENDS HER BACK (ops #46): a page of ours, beside her invoice's `/r/<token>` and like it
 * answered with no sign-in, on the host her page was asked from. Every page Stripe makes for this
 * business names it: `success_url` is the path, and thanks her; `cancel_url` is the path with
 * `PAYMENT_NOT_PAID` in the query, where Stripe's back arrow takes her if she leaves without paying,
 * and says she has not paid yet. Stripe keeps both addresses on each session it makes, so neither
 * carries a token, an id or anything of hers, and the page reads nothing and names nobody.
 */
export const PAYMENT_RETURN_PATH = '/r/payment';
export const PAYMENT_NOT_PAID = 'not-paid';

/**
 * WHAT THE PAGE SHE PAID ON COULD DO, in `success_url`'s query (the staging walk of 2026-09-23): the
 * page she came back to spoke only of a bank payment, after a card too, and said nothing when she had
 * saved a method. Stripe sends her to the one address a page was made with, whatever she did on it, so
 * the address says what the page could do, as Stripe was asked to make it, in words that name nobody:
 * `PAYMENT_RETURN_PAGE` is the page's kind (`PAY_PAGE_KINDS`), and `PAYMENT_RETURN_SAVES` what it could
 * save (`PAYMENT_RETURN_SAVING`), absent where it saved nothing: `account` for a bank page her own tick
 * asked to save the account she pays from, and `box` for a page offering Stripe's save box, which only
 * Stripe knows whether she ticked. An address with neither, a page made before these, says only what is
 * true of every page.
 */
export const PAYMENT_RETURN_PAGE = 'page';
export const PAYMENT_RETURN_SAVES = 'saves';
export const PAYMENT_RETURN_SAVING = values(['account', 'box']);
export type PaymentReturnSaving = (typeof PAYMENT_RETURN_SAVING)[number];

/**
 * A CHARGE WAITS FOR HER WORD (ops #46, step 4, migration `0021`). An invoice issued for a household
 * whose saved method stands carries one `invoice_charges` row, and these four lists are its CHECKs,
 * decided in full now because widening one is a rebuild (`docs/gotchas.md` entry 34). The hold reasons
 * gained `paying_by_zelle` on 2026-09-23 while `0021` was still unreleased, in the file itself.
 *
 * Where it stands. `waiting` for her answer; `held` while something stops it (the reason beside it);
 * `due`, a gate opened on a run whose charges were spent, carried now and written by nothing yet,
 * because this build posts in the run the gate opens; `charging` from the claim until Stripe answers,
 * and after a card's answer until Stripe's event says the money arrived; `processing` while a bank
 * debit clears; then one of the ends: `succeeded`, the money recorded; `declined` by the bank or the
 * card; `needs_authentication`, her bank wants her to confirm it herself; `failed`, Stripe never
 * answered after every try or refused the request; `cancelled`, nothing to charge any more.
 */
export const INVOICE_CHARGE_STATES = values([
  'waiting',
  'held',
  'due',
  'charging',
  'processing',
  'succeeded',
  'declined',
  'needs_authentication',
  'failed',
  'cancelled',
]);
export type InvoiceChargeState = (typeof INVOICE_CHARGE_STATES)[number];

/**
 * What opened the gate, and there are only two: her good answer, or the office releasing a charge
 * that is not the first under her authorization. SILENCE IS NOT ONE, and never will be: the owner
 * decided on 2026-09-18 that silence never counts as a good answer, so there is no word for it here
 * and no clock on the row that could open a charge by itself.
 */
export const CHARGE_GATE_REASONS = values(['answered_well', 'office_released']);
export type ChargeGateReason = (typeof CHARGE_GATE_REASONS)[number];

/**
 * Why it is held: an issue is open on the clean or her answer opened one; she replied in words a
 * person has to read (ops #25, written by nothing yet); the message asking her never arrived, so she
 * was never asked; the office held the question on the invoice; or she pressed "I'm paying by Zelle"
 * on her page (the owner, 2026-09-23), which holds the charge of that one invoice until her Zelle is
 * recorded or the office lifts the pause.
 */
export const CHARGE_HOLD_REASONS = values([
  'issue_reported',
  'reply_received',
  'survey_not_delivered',
  'office_hold',
  'paying_by_zelle',
]);
export type ChargeHoldReason = (typeof CHARGE_HOLD_REASONS)[number];

/**
 * WHERE HER INVOICE'S CHARGE STANDS, AS HER PAGE SAYS IT (ops #46, the owner's decision of 2026-09-23),
 * while her saved method pays the invoice. `waiting` for her word, and she may still say she is paying
 * this one by Zelle; `paying_by_zelle`, she said so, and nothing is charged while the pause stands;
 * `pause_lifted`, the office lifted it, and the charge goes ahead as before; `at_stripe`, the charge is
 * being made, so a Zelle now would pay the invoice twice. Words of a response, never a column.
 */
export const HER_CHARGE_STAGES = values([
  'waiting',
  'paying_by_zelle',
  'pause_lifted',
  'at_stripe',
]);
export type HerChargeStage = (typeof HER_CHARGE_STAGES)[number];

/**
 * HOW LONG A PAUSE FOR HER ZELLE WAITS BEFORE THE OFFICE IS TOLD (the owner, 2026-09-23): three
 * business days, counted on the business days the owner set (`business_days`), from the moment she
 * pressed. Past them with the invoice still owed, the automatic charge's job raises one task for the
 * office; a Zelle recorded before then leaves nothing owed, and the charge is cancelled as it always is.
 */
export const ZELLE_PAUSE_BUSINESS_DAYS = 3;

/**
 * Why there is nothing to charge any more: the invoice was paid another way, Zelle above all, or it
 * owes nothing; it was voided; the saved method was stopped; or the office said not to charge this
 * one.
 */
export const CHARGE_CANCEL_REASONS = values([
  'paid_another_way',
  'invoice_void',
  'authorization_revoked',
  'office_cancelled',
]);
export type ChargeCancelReason = (typeof CHARGE_CANCEL_REASONS)[number];

/**
 * WHAT A DOCUMENT IS (ops #76 O3, migration `0034`): the PDF ops renders once and keeps. `invoice` at
 * an invoice's first send, `cancellation` when a numbered invoice is voided, `receipt` when a payment
 * is recorded. `refund` and `statement` are decided now and written by nothing yet, because a CHECK
 * widened later is a rebuild (`docs/gotchas.md` entry 34): a refund's PDF and a household's statement
 * are phase 4's. `ck_documents_kind` is built from this list; `ck_documents_about` says which row each
 * kind is of.
 */
export const DOCUMENT_KINDS = values(['invoice', 'cancellation', 'receipt', 'refund', 'statement']);
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/**
 * Where a document stands: `pending` from the press until its bytes are kept, `rendered` once they
 * are and for good, `failed` after its third attempt until the office tries again, and `forgotten`
 * once a forget has taken its words. Nothing moves a `rendered` document but to `forgotten`.
 */
export const DOCUMENT_STATES = values(['pending', 'rendered', 'failed', 'forgotten']);
export type DocumentState = (typeof DOCUMENT_STATES)[number];

/* ------------------------------------------------------------------ *
 * G. The crew
 * ------------------------------------------------------------------ */

export const CLEANER_STATUSES = values(['applicant', 'auditioning', 'active', 'paused', 'ended']);
export type CleanerStatus = (typeof CLEANER_STATUSES)[number];

/**
 * Tax classification, which is a different question from authorization role and never merges with
 * it. The words themselves are chosen to keep the forbidden ones out of the database: the tax
 * forms are what these mean, and `classification_changed_on` exists because a mid-year conversion
 * splits the tax year and the accountant will ask.
 */
export const CLASSIFICATIONS = values(['contractor_1099', 'payroll_w2']);
export type Classification = (typeof CLASSIFICATIONS)[number];

/**
 * `erd.md` Part 2: no child line table. A deduction is its own row with `kind = correction` and a
 * negative amount, because an unexplained smaller number on a 1099 contractor's pay is exactly the
 * thing a classification review asks about.
 */
export const CLEANER_PAYMENT_KINDS = values([
  'per_clean',
  'guarantee',
  'tip_pass_through',
  'reimbursement',
  'correction',
]);
export type CleanerPaymentKind = (typeof CLEANER_PAYMENT_KINDS)[number];

/* ------------------------------------------------------------------ *
 * H. Communication, obligation and governance
 * ------------------------------------------------------------------ */

/**
 * Operational only. Marketing, promotions, availability prospecting and review requests are
 * forbidden outright by the brief, so there is no template kind that could carry one.
 *
 * THE LAST FIVE ARRIVED WITH MIGRATION `0023` (ops #51), and only the first of them is composed by
 * anything: `opt_in_confirmation`, the filed first text to a number, word for word as
 * `OPT_IN_CONFIRMATION_WORDS` below. `opt_out_confirmation`, `reply`, `visit_finished` and
 * `invitation` are the rest of the phone system's filed vocabulary and the client system's word,
 * decided now because this list is a CHECK on two tables and a word added later is a second rebuild
 * of both while they are live (gotcha 34). A word nobody composes under costs nothing.
 *
 * THE TWELVE AFTER THEM ARRIVED WITH MIGRATION `0039` (ops #76 O6, ops #78), in the CHECK's order:
 * `visit_reminder`, the reminder before a visit, by text (`OWNER_ACCEPTED_SMS_KINDS` below) and by
 * email; `payment_receipt`, `invoice_cancelled`, `refund_notice`, `invoice_overdue`, the past-due
 * note, and `payment_problem`, O6's emails, every one in `EMAIL_KINDS` below; and the six after them,
 * `autopay_changed` to `statement`, the later phases' words of `docs/seams/home.md` 11.1, composed by
 * nothing yet. Every word a later phase names
 * is here so no phase rebuilds the two tables again, BUT `review_ask`: CLAUDE.md refuses review
 * requests, and the sentence above is a property of the schema. A revision that builds one, if the
 * business brief ever changes, carries its own rebuild.
 */
export const TEMPLATE_KINDS = values([
  'booking_confirmation',
  'schedule_change',
  'arrival_notice',
  'access_problem',
  'invoice_sent',
  'reclean_offer',
  'visit_report_ready',
  'opt_in_confirmation',
  'opt_out_confirmation',
  'reply',
  'visit_finished',
  'invitation',
  'visit_reminder',
  'payment_receipt',
  'invoice_cancelled',
  'refund_notice',
  'invoice_overdue',
  'payment_problem',
  'autopay_changed',
  'price_change',
  'person_added',
  'contact_changed',
  'damage_report',
  'statement',
]);
export type TemplateKind = (typeof TEMPLATE_KINDS)[number];

export const TEMPLATE_VERSION_STATES = values(['draft', 'approved', 'retired']);
export type TemplateVersionState = (typeof TEMPLATE_VERSION_STATES)[number];

export const MESSAGE_CHANNELS = values(['sms', 'email', 'call']);
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];

/**
 * How a phone call finished, VERBATIM from `faithfully-kept-phone/docs/api/facts.md`: the five
 * outcomes a `call.ended` fact carries. Copied rather than paraphrased, because the phone system
 * is the only thing that decides this and a synonym invented here would be a second vocabulary
 * for the same fact.
 *
 * Exactly one arrives per call, and it is the first outcome decided. An outcome can improve
 * internally, from `missed` to `voicemail` when a caller leaves a message, and ops is
 * deliberately not told twice: a `transcript.ready` later is how it learns there was a message.
 */
export const CALL_OUTCOMES = values(['answered', 'voicemail', 'missed', 'abandoned', 'blocked']);
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

/**
 * Composed here, never sent here. `handed_off` is the last state ops controls; everything after it
 * is mirrored back from the phone system, which owns delivery.
 */
export const MESSAGE_INTENT_STATES = values([
  'composed',
  'approved',
  'handed_off',
  'delivered',
  'failed',
  'cancelled',
  'refused_by_consent',
]);
export type MessageIntentState = (typeof MESSAGE_INTENT_STATES)[number];

export const OUTBOUND_STATES = values(['queued', 'sent', 'delivered', 'failed']);
export type OutboundState = (typeof OUTBOUND_STATES)[number];

/**
 * THE THREE KEYWORDS A TEXT IN CAN CARRY, VERBATIM from `faithfully-kept-phone/docs/api/facts.md`:
 * a `message.received` fact names `stop`, `start` or `help`, or null for an ordinary text (ops #25,
 * migration `0024`). The phone system decides it and answers HELP itself; a stop and a start arrive
 * again as facts of their own, so a text carrying one is kept and raises nothing.
 *
 * A KEYWORD IS THE CARRIER'S WHOLE-BODY MATCH, and nothing else. `Stop.`, `STOP!!` or `stop texting
 * me` defeats it and arrives with no keyword; since ops #30 ops reads such a body once, for a stop in
 * her own words (`api/src/schedule/stop-words.ts`), and only when the keyword is null, so one text is
 * never acted on twice.
 */
export const INBOUND_KEYWORDS = values(['stop', 'start', 'help']);
export type InboundKeyword = (typeof INBOUND_KEYWORDS)[number];

/**
 * WHERE A STOP IN HER OWN WORDS REACHES (ops #30, migration `0031`): texts, under the owner's D5, or
 * every channel for the few phrases that ask to be left alone. A CHECK on `stop_holds.words_channel`.
 */
export const STOP_WORDS_CHANNELS = values(['sms', 'all']);
export type StopWordsChannel = (typeof STOP_WORDS_CHANNELS)[number];

/**
 * HOW A HELD STOP ENDS (ops #30, migration `0031`): the office `confirmed` it as a stop, or `cleared`
 * it as no stop at all; nobody decided in five business days, so it was `auto_suppressed`; or a mark
 * that already says no in her own words, her STOP, a stop she texted plainly or her forget, made it
 * moot, `superseded`. A CHECK on `stop_holds.outcome`, null while the hold is open.
 */
export const STOP_HOLD_OUTCOMES = values(['confirmed', 'cleared', 'auto_suppressed', 'superseded']);
export type StopHoldOutcome = (typeof STOP_HOLD_OUTCOMES)[number];

/**
 * Where a text in stands (ops #25, migration `0024`): `open` until a person has dealt with it, then
 * `handled`, or `spam` for a text that was never a person. Each moves once, from `open`, and says
 * when; the actions that move it are ops #26.
 */
export const INBOUND_MESSAGE_STATES = values(['open', 'handled', 'spam']);
export type InboundMessageState = (typeof INBOUND_MESSAGE_STATES)[number];

/**
 * WHY A MESSAGE DID NOT GO, in the phone system's own words (ops #23, migration `0017`), VERBATIM
 * from `faithfully-kept-phone/docs/api/facts.md`, because the phone system is the only thing that
 * decides it and a synonym invented here would be a second vocabulary for the same fact.
 *
 * Two lists, one CHECK. The eleven a `message.failed` fact carries, when the phone system took the
 * instruction and the message then did not arrive, in the order the contract lists them:
 */
export const DELIVERY_FAILURE_CODES = values([
  'not_enabled',
  'recipient_stopped',
  'number_held',
  'first_text_needs_opt_out',
  'daily_cap',
  'expired',
  'carrier_filtered',
  'undeliverable',
  'invalid_number',
  'unconfirmed',
  'cancelled',
]);
export type DeliveryFailureCode = (typeof DELIVERY_FAILURE_CODES)[number];

/**
 * And the nine the phone system refuses an instruction with and calls final, for ops to park:
 * `400 bad_json`, `409 key_reused` and the seven `422` codes. The answers it asks ops to retry, a
 * `401`, a `403`, a `404`, a `500` or a `503`, are not here, because they are not why a message did
 * not go; they are why it has not gone yet.
 *
 * AN EIGHTH `422` IS NOT HERE, ON PURPOSE (ops #55): `not_as_filed`, added to the contract on
 * 2026-09-23 for an `opt_in_confirmation` not in the filed words. This list backs the CHECK on both
 * `failure_code` columns, and widening either is a rebuild (gotcha 34), so it is recorded the way a
 * word ops does not hold is: `invalid`, with the word and the phone system's sentence kept whole in
 * the outbox's last error and the owners' notice. It cannot occur while ops sends
 * `OPT_IN_CONFIRMATION_WORDS`, and `api/test/opt-in-first.test.ts` holds how it lands.
 */
export const REFUSAL_CODES = values([
  'bad_json',
  'key_reused',
  'invalid',
  'unknown_instruction',
  'kind_not_filed',
  'basis_missing',
  'no_business_name',
  'too_long',
  'link_not_allowed',
]);
export type RefusalCode = (typeof REFUSAL_CODES)[number];

/**
 * The twenty, on `message_intents.failure_code` and `outbound_messages.failure_code`. Decided in
 * full now, because each is a CHECK and widening one later is a rebuild (gotcha 34), and holding no
 * word of ops's own. A code the phone system adds later is stored as `undeliverable` when a fact
 * carries it and `invalid` when a refusal does, with the word as it came kept beside it in the
 * failure reason or the outbox's last error, so the list never has to widen for the vocabulary to
 * grow on the other side.
 */
export const FAILURE_CODES = values([...DELIVERY_FAILURE_CODES, ...REFUSAL_CODES]);
export type FailureCode = (typeof FAILURE_CODES)[number];

/**
 * The kinds of text the carrier approved in the texting campaign, from the same contract. The phone
 * system refuses an sms under any other kind with `422 kind_not_filed`, save the few the owner
 * accepted outside the filed words (`OWNER_ACCEPTED_SMS_KINDS` below), and an approved campaign
 * cannot gain a use case. So every kind ops composes a message under must be here or on that list,
 * which is `SENDABLE_SMS_KINDS`, and `api/test/seams.test.ts` holds every site that composes one to
 * it.
 *
 * Copied without two of the contract's words. `sign_in_code` is refused from ops with `422 invalid`,
 * because only the client site's own route may send one. `invitation` is leaving: the final filing
 * text leaves it out, and from the commit that records the campaign the phone system refuses it as
 * not filed. Not a CHECK, so this list changes with the contract and costs no migration.
 */
export const FILED_SMS_KINDS = values([
  'opt_in_confirmation',
  'booking_confirmation',
  'schedule_change',
  'visit_finished',
  'visit_report_ready',
  'access_problem',
  'arrival_notice',
  'invoice_sent',
  'reply',
  'opt_out_confirmation',
]);
export type FiledSmsKind = (typeof FILED_SMS_KINDS)[number];

/**
 * THE KINDS THE OWNER ACCEPTED OUTSIDE THE FILED WORDS (ops #78), ops's copy of the list the phone
 * system keeps as `OWNER_ACCEPTED_KINDS`, in its words: "By the owner's decision of 2026-10-17,
 * 'Website box only, no refile', a reminder goes by text to the people who ticked the box on the
 * website's quote form, under the approval the campaign has, although the filed description does not
 * name reminders: a gap in the words the owner accepts. It is not filed, so it is not in the list
 * above, which records the filing; we keep it on a list of its own" (faithfully-kept-phone#27,
 * `docs/seams/phone.md` section 4). So it is never added to `FILED_SMS_KINDS`, and a text under it
 * goes exactly as a `booking_confirmation` does. Not a CHECK: it changes with the contract and costs
 * no migration.
 */
export const OWNER_ACCEPTED_SMS_KINDS = values(['visit_reminder']);
export type OwnerAcceptedSmsKind = (typeof OWNER_ACCEPTED_SMS_KINDS)[number];

/**
 * Every kind the phone system takes a text under: the filed kinds, then the ones the owner accepted
 * outside them. `api/test/seams.test.ts` holds every site that composes an intent to this list.
 */
export const SENDABLE_SMS_KINDS = values([...FILED_SMS_KINDS, ...OWNER_ACCEPTED_SMS_KINDS]);
export type SendableSmsKind = (typeof SENDABLE_SMS_KINDS)[number];

/**
 * THE KINDS THE OFFICE COMPOSES IN ITS OWN WORDS (ops #32), each filed with the carrier and each in
 * `ck_message_intents_kind` since migration `0023`: the composer's door takes these and no other, and
 * the screen offers these. `arrival_notice` and `visit_report_ready` are filed and not offered: the
 * issue says later, and an arrival notice has a consent purpose of its own.
 */
export const COMPOSER_KINDS = values([
  'reply',
  'booking_confirmation',
  'visit_finished',
  'access_problem',
]);
export type ComposerKind = (typeof COMPOSER_KINDS)[number];

/**
 * THE KINDS AN `email` PUSH MAY CARRY IN THIS PHASE (ops #76 O6, `docs/plan-o6-o9-emails.md` section
 * 3.2), which the composer and the drain hold to: the seven emails O6 composes, then the five an
 * email can already be, in the office's own words. Home sends nothing of a kind it does not know,
 * `failed` with `kind_not_known` (`docs/seams/home.md` 11.1), so a word is added here only once home
 * knows it. Every one is a `TEMPLATE_KINDS` word both tables admit; the later phases' words wait in
 * the CHECK and are not here. Not a CHECK itself: it changes with the contract and costs no migration.
 */
export const EMAIL_KINDS = values([
  'invoice_sent',
  'payment_receipt',
  'invoice_cancelled',
  'refund_notice',
  'payment_problem',
  'invoice_overdue',
  'visit_reminder',
  'schedule_change',
  'reply',
  'booking_confirmation',
  'visit_finished',
  'access_problem',
] as const satisfies readonly TemplateKind[]);
export type EmailKind = (typeof EMAIL_KINDS)[number];

/**
 * THE CHOICE ROWS A MESSAGE FALLS UNDER, as an `email` push names its `category` (`docs/seams/home.md`
 * 11.1 and 11.3): the rows a client sets her choices on, and `reply` for the office's answer to her
 * own ask, which is not a choice. Home's phase 4 names `reviews` as well, and it is not here: CLAUDE.md
 * refuses review requests, as `TEMPLATE_KINDS` does.
 */
export const MESSAGE_CATEGORIES = values([
  'visit_changes',
  'visit_reminders',
  'visit_day',
  'home_checks',
  'invoices',
  'receipts',
  'account',
  'reply',
]);
export type MessageCategory = (typeof MESSAGE_CATEGORIES)[number];

/**
 * THE CATEGORY OF EACH EMAIL KIND, the map of `docs/plan-o6-o9-emails.md` section 1: every money
 * email but the receipt under `invoices`, the receipt under `receipts`, the reminder under
 * `visit_reminders`, and the office's own words where home files them. The composer reads it; the
 * push carries the word.
 */
export const CATEGORY_OF_KIND: Readonly<Record<EmailKind, MessageCategory>> = Object.freeze({
  invoice_sent: 'invoices',
  payment_receipt: 'receipts',
  invoice_cancelled: 'invoices',
  refund_notice: 'invoices',
  payment_problem: 'invoices',
  invoice_overdue: 'invoices',
  visit_reminder: 'visit_reminders',
  schedule_change: 'visit_changes',
  reply: 'reply',
  booking_confirmation: 'visit_changes',
  visit_finished: 'visit_day',
  access_problem: 'visit_changes',
});

/**
 * THE CATEGORY OF EACH KIND THAT GOES ONLY AS A TEXT (`docs/seams/home.md` 11.4, ops #76 O6): the
 * kinds the phone system takes a text under and no email of this phase carries, so a text's copy is
 * shown and filtered under a row as an email is. The two confirmations of her texting are `account`,
 * the row that is always on, and the two words about a visit's own day are `visit_day`. Every other
 * text kind is an email kind too, and takes the word its email carries.
 */
const CATEGORY_OF_TEXT_ONLY_KIND = {
  opt_in_confirmation: 'account',
  opt_out_confirmation: 'account',
  visit_report_ready: 'visit_day',
  arrival_notice: 'visit_day',
} as const satisfies Record<Exclude<SendableSmsKind, EmailKind>, MessageCategory>;

/**
 * THE CATEGORY OF EVERY MESSAGE KIND A COPY MAY CARRY: an email's from `CATEGORY_OF_KIND`, and a
 * text's the same word, so one kind falls under one row on either channel. A visit's reminder is
 * `visit_reminders` whatever the visit, a home watch visit's too, since that row is where her lead
 * days are chosen.
 */
export const CATEGORY_OF_MESSAGE_KIND: Readonly<
  Record<EmailKind | SendableSmsKind, MessageCategory>
> = Object.freeze({ ...CATEGORY_OF_KIND, ...CATEGORY_OF_TEXT_ONLY_KIND });

/**
 * THE ROW A MESSAGE OF THIS KIND FALLS UNDER, or null for a kind no message of this phase is composed
 * under (an old `invitation`, a later phase's word): what a `message` copy names as its `category`.
 */
export function categoryOfMessage(kind: string): MessageCategory | null {
  return Object.hasOwn(CATEGORY_OF_MESSAGE_KIND, kind)
    ? CATEGORY_OF_MESSAGE_KIND[kind as keyof typeof CATEGORY_OF_MESSAGE_KIND]
    : null;
}

/*
 * THE CLIENT'S CHOICES ABOUT MESSAGES (ops #76 phase 2, O10 to O13; migration `0050`,
 * `docs/plan-preferences.md` section 3, `docs/seams/home.md` 11.3). One append-only table,
 * `message_preferences`, holds every choice, hold, pause, quiet window and summary row ever written,
 * the latest per its own key holding; what nobody chose is D10's default, computed at every read. The
 * first eight lists are CHECKs on that table, decided in full now because a word added later is a
 * rebuild (gotcha 34); the last four are words of the `preferences` push, lists and types only.
 */

/** The five kinds of row the table holds, each the latest per its own key. */
export const PREFERENCE_ROW_KINDS = values(['choice', 'hold', 'pause', 'quiet', 'summary']);
export type PreferenceRowKind = (typeof PREFERENCE_ROW_KINDS)[number];

/**
 * THE ROWS A CHOICE IS SET ON: `MESSAGE_CATEGORIES` less `account`, which has no switch and is never
 * written, and `reply`, the office's answer to her own ask, which is not a choice; and `reviews`, in
 * the CHECK now so phase 4 is no rebuild, refused at every door until then.
 */
export const PREFERENCE_CATEGORIES = values([
  'visit_changes',
  'visit_reminders',
  'visit_day',
  'home_checks',
  'invoices',
  'receipts',
  'reviews',
]);
export type PreferenceCategory = (typeof PREFERENCE_CATEGORIES)[number];

/** An email choice, on or off: the contract's `not_offered` is never stored. */
export const PREFERENCE_EMAIL_CHOICES = values(['on', 'off']);
export type PreferenceEmailChoice = (typeof PREFERENCE_EMAIL_CHOICES)[number];

/** Who wrote a row: the person, the office, ops itself, or her STOP. */
export const PREFERENCE_CHANGED_BY = values(['client', 'office', 'system', 'stop']);
export type PreferenceChangedBy = (typeof PREFERENCE_CHANGED_BY)[number];

/** How it reached ops, in the contract's words. */
export const PREFERENCE_HOW = values([
  'home',
  'call',
  'email',
  'text',
  'in_person',
  'stop',
  'start',
  'lock',
]);
export type PreferenceHow = (typeof PREFERENCE_HOW)[number];

/** The parts of the day a reminder goes at, the owner's setting's words as well (ops #78). */
export const SEND_TIMES = values(['morning', 'afternoon', 'evening']);
export type SendTime = (typeof SEND_TIMES)[number];

/** Each message as it comes, or one summary a day (phase 6). */
export const SUMMARY_KINDS = values(['each', 'daily']);
export type SummaryKind = (typeof SUMMARY_KINDS)[number];

/** Where a person's texts stand, as the push says it: from the register, never from a row. */
export const TEXTS_STATES = values([
  'on',
  'off_by_choice',
  'off_by_stop',
  'marked',
  'no_consent',
  'none',
]);
export type TextsState = (typeof TEXTS_STATES)[number];

/**
 * Where a person's email stands, as the push says it. `no_consent` is ops's revision of 11.3 (an
 * address on file with no yes to email on the register); `bouncing` is never sent in this build.
 */
export const EMAIL_STATES = values(['on', 'none', 'marked', 'no_consent', 'bouncing']);
export type EmailState = (typeof EMAIL_STATES)[number];

/** How texts that are off come back on: the website's box, home's box after a refiling, or START. */
export const TURN_ON_BY = values(['website_box', 'home_box', 'keyword']);
export type TurnOnBy = (typeof TURN_ON_BY)[number];

/** Why a row's email switch may not simply go off: one way must stay open, or `account`'s email. */
export const LOCKS = values(['one_way', 'email']);
export type Lock = (typeof LOCKS)[number];

/**
 * HOW A PERSON TOLD THE OFFICE, the words an office row of `message_preferences` may carry for a choice
 * it writes on their word (phase 4): `PREFERENCE_HOW` less the door `home` and the phone system's and
 * the lock's own words. A list and a type, never a CHECK: the column's CHECK is `PREFERENCE_HOW`. A hold
 * or a lift the office decides itself, with nobody asking, is written `lock`, "as the office recorded
 * it".
 */
export const OFFICE_PREFERENCE_HOW = values(['call', 'email', 'text', 'in_person']);
export type OfficePreferenceHow = (typeof OFFICE_PREFERENCE_HOW)[number];

/**
 * THE OPT-OUT LINE, as the contract defines it (ops #23). A text carries it when it contains "stop
 * to opt out", compared in lower case, and the first text to a number must carry it or the phone
 * system fails it `first_text_needs_opt_out`. Only the phone system knows which text is the first,
 * and only when the text goes, so every text a composer here offers ends with it; an email never
 * does. A person may still delete it, and the phone system's check at send time is the control.
 */
export const OPT_OUT_LINE = 'Reply STOP to opt out.';

/**
 * THE BUSINESS LINE, the one number a client is told to call or text: published on the website and
 * on every card, and answered by the phone system. Written once for both halves, as a phone dials it
 * and as a person reads it back, so a message the Worker composes and a page the SPA shows her name
 * the same number (ops #46). `web/src/format.test.ts` holds the two to each other.
 */
export const BUSINESS_PHONE_E164 = '+17273377775';
export const BUSINESS_PHONE_DISPLAY = '(727) 337-7775';

export const CONSENT_PURPOSES = values([
  'operational_sms',
  'operational_email',
  'arrival_notice',
  'visit_photos',
]);
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];

export const CONSENT_KINDS = values(['granted', 'withdrawn']);
export type ConsentKind = (typeof CONSENT_KINDS)[number];

/**
 * `verbal_on_call` is in this list because the first client the business ever books consents on a
 * phone call and never sees the booking form. A register that cannot record that is a register
 * that will be bypassed on day one.
 *
 * FOR TEXTS, ONLY THE FIRST COUNTS, since the owner's decision of 2026-09-23 (ops #51): the carrier
 * approved the texting campaign on the promise that people opt in one way only, the box on the
 * website quote form, so a texting grant any other way is recorded by nobody and read as nothing
 * (`schedule/consent.ts`). And `booking_form` is written by the system alone, from what the form
 * captured, never typed in by a person. Every method still records an email consent, and every
 * method records a withdrawal of either, because a refusal is always honoured.
 */
export const CAPTURE_METHODS = values([
  'booking_form',
  'verbal_on_call',
  'email_reply',
  'sms_reply',
  'in_person',
  'written',
]);
export type CaptureMethod = (typeof CAPTURE_METHODS)[number];

/**
 * THE WORDING BESIDE THE BOX the carrier approved the campaign on (ops #51,
 * FaithfullyKept/site-faithfully-kept#10), as the website names it on every lead push. A texting
 * grant under any other wording is no grant. Not a CHECK: a later wording is a new value here and a
 * deploy, and the rows under the old one stay what they were.
 */
export const SMS_CONSENT_WORDING = 'sms-2026-09-17.1';

/**
 * THE TWO PAGES THE BOX IS ON, and the only two a tick counts from: the booking page and the deep
 * clean landing page. The home watch page lost its box on 2026-09-23. Compared as origin plus path
 * with a trailing slash dropped on both sides, which is how `seams/leads.ts` keeps a page address
 * (`consentPageCounts` in `schedule/consent.ts`).
 */
export const SMS_CONSENT_PAGES = values([
  'https://faithfullykept.com/book/',
  'https://faithfullykept.com/lp/deep-clean/',
]);

/**
 * WHY THE RE-GRANT FROM THE WEBSITE TICK PASSES SOMEBODY BY (ops #55, item 5), in the order the rule
 * asks, so the first that applies is the one reported: the household, the tick itself, whether
 * anybody on the household holds its number now, the person, and then every later no she has said,
 * which the grant never overrides. `api/src/schedule/texting-regrant.ts` asks them; the Settings
 * screen reads their words from `TEXTING_REGRANT_SKIP_LABELS`. Not a CHECK: nothing stores one.
 */
export const TEXTING_REGRANT_SKIPS = values([
  'household_removed',
  'household_ended',
  'wording_not_filed',
  'page_not_counted',
  'number_unknown',
  'number_changed',
  'forgotten',
  'contact_removed',
  'already_granted',
  'withdrawn_later',
  'unticked_later',
  'carrier_stop',
  'do_not_contact',
  'suppressed',
]);
export type TextingRegrantSkip = (typeof TEXTING_REGRANT_SKIPS)[number];

/**
 * THE FIRST TEXT TO EVERY NUMBER, and the first after any stop, word for word as the campaign filed
 * it (ops #51). The phone system refuses an `opt_in_confirmation` in any other words with
 * `422 not_as_filed`. It carries "stop to opt out" in lower case, which is the contract's own test
 * for the opt-out line.
 */
export const OPT_IN_CONFIRMATION_WORDS =
  'Faithfully Kept: you will get texts about your cleaning visits, and replies to your messages. Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out.';

/**
 * THE GO-LIVE SWITCH FOR TEXTS (ops #31), where an owner leaves `sms_go_live` on the Settings screen:
 * off, texting the listed test handsets and nobody else, or on. `on` counts only while the campaign's
 * approval and the form's go-live are both recorded (`api/src/schedule/sms-gate.ts`). Not a CHECK: a
 * setting is a key and a value, and this list is the registry's to enforce at the door.
 */
export const SMS_GO_LIVE_STATES = values(['off', 'test_handsets', 'on']);
export type SmsGoLiveState = (typeof SMS_GO_LIVE_STATES)[number];

/**
 * THE GO-LIVE SWITCH FOR CLIENT EMAIL (ops #76 O6), where an owner leaves `email_go_live` on the
 * Settings screen, the texting switch's twin (`docs/plan-o6-o9-emails.md` section 3.4): off, emailing
 * the listed test addresses and nobody else, or on, which counts only once the DMARC day is
 * recorded. Not a CHECK: a setting is a key and a value, and this list is the registry's to enforce.
 */
export const EMAIL_GO_LIVE_STATES = values(['off', 'test_addresses', 'on']);
export type EmailGoLiveState = (typeof EMAIL_GO_LIVE_STATES)[number];

export const SUPPRESSION_SCOPES = values(['all', 'sms', 'email', 'call']);
export type SuppressionScope = (typeof SUPPRESSION_SCOPES)[number];

export const SUPPRESSION_SOURCES = values([
  'client_request',
  'carrier_stop',
  'owner_decision',
  'bounce',
]);
export type SuppressionSource = (typeof SUPPRESSION_SOURCES)[number];

/**
 * THE DO-NOT-CONTACT REGISTER (ops #44, migration `0016`): a mark, set or lifted, on a person, a
 * number or an email, with who, when and why on every row. Decided in full on day one, because each
 * of these lists is a CHECK and widening one later is a rebuild (gotcha 34). The channel a mark
 * covers is `SUPPRESSION_SCOPES`, reused rather than copied: `call` is voice, and `all` is every
 * channel, including any added later. There is no post: ops sends none.
 */
export const DO_NOT_CONTACT_TARGETS = values(['contact', 'phone', 'email']);
export type DoNotContactTarget = (typeof DO_NOT_CONTACT_TARGETS)[number];

/** A lift is a row of its own, so who lifted a mark and why is kept like who set it. */
export const DO_NOT_CONTACT_KINDS = values(['set', 'lifted']);
export type DoNotContactKind = (typeof DO_NOT_CONTACT_KINDS)[number];

/**
 * Which door the mark came through. `office` is a signed-in person, and the row says which; the
 * others may have nobody at them. `home` is the client's own door of roadmap D, carried now because
 * a word added to a CHECK later is a rebuild and a word nobody uses costs nothing.
 */
export const DO_NOT_CONTACT_DOORS = values(['office', 'website', 'phone', 'home', 'system']);
export type DoNotContactDoor = (typeof DO_NOT_CONTACT_DOORS)[number];

/**
 * Why, one list for sets and lifts. The four `SUPPRESSION_SOURCES` words are kept verbatim, so a
 * stop means the same thing in both registers. `forget_request` is written by the forget, at either
 * door; `came_back` and `recorded_in_error` are the words a lift carries.
 */
export const DO_NOT_CONTACT_REASONS = values([
  'client_request',
  'forget_request',
  'carrier_stop',
  'bounce',
  'wrong_number',
  'owner_decision',
  'came_back',
  'recorded_in_error',
]);
export type DoNotContactReason = (typeof DO_NOT_CONTACT_REASONS)[number];

/**
 * WHY THE OFFICE MAY SET A MARK, at its own door (ops #44 step two): every set word the register
 * holds but two. `forget_request` is written by the forget, which blanks her in the same batch, and
 * `carrier_stop` by her STOP from the phone system, which is her own word; neither is a word a
 * person types. Not a CHECK: the door enforces it, and the register's CHECK holds every word.
 */
export const OFFICE_MARK_REASONS = values([
  'client_request',
  'bounce',
  'wrong_number',
  'owner_decision',
] as const satisfies readonly DoNotContactReason[]);
export type OfficeMarkReason = (typeof OFFICE_MARK_REASONS)[number];

/**
 * WHY A MARK IS LIFTED, the two words a lift carries: she came back and asked to hear from us, or
 * the mark should never have been set. A STOP at the carrier is lifted only as `came_back`, because
 * it is her own word, and whoever lifts it says she asked for texts again (ops #44, ops #62).
 */
export const LIFT_REASONS = values([
  'came_back',
  'recorded_in_error',
] as const satisfies readonly DoNotContactReason[]);
export type LiftReason = (typeof LIFT_REASONS)[number];

/**
 * WHO MAY LIFT A MARK: an owner or a manager, with the same typed reason and the same record of who,
 * when and why (the owner, 2026-10-15; step two of ops #44 built it an owner's alone, the design's
 * default). The office sets a mark and never lifts one, and a cleaner reaches neither. A list and
 * never a CHECK: the server holds the door to it (`api/src/routes/do-not-contact.ts`), and the screen
 * offers Lift by the same list, which is UX and not the control.
 */
export const MARK_LIFTING_ROLES = values(['owner', 'manager'] as const satisfies readonly Role[]);
export type MarkLiftingRole = (typeof MARK_LIFTING_ROLES)[number];

/** Whether this role may lift a do-not-contact mark, as the server decides it. */
export const mayLiftMarks = (role: Role): boolean =>
  (MARK_LIFTING_ROLES as readonly Role[]).includes(role);

/**
 * HOW DID WE DO (ops #46, migration `0019`). The invoice message asks it, and her answer is one of
 * four faces, written as a row of `visit_feedback` wherever it arrives. Every list below is a CHECK,
 * and widening one later is a rebuild (gotcha 34), so each is decided in full now.
 *
 * Where an answer came from. `link` is her invoice's own link; `home` is the client's door of roadmap
 * D and `text` a reply by text once ops #25 reads them, both carried now and written by nothing yet;
 * `office` is a person at the office recording what she said on the phone, and that row names them.
 */
export const FEEDBACK_SOURCES = values(['link', 'home', 'office', 'text']);
export type FeedbackSource = (typeof FEEDBACK_SOURCES)[number];

/**
 * The four faces, best first. Two are good and two are not, and nothing in between: an answer below
 * `happy` opens an issue and a follow-up for the office, whatever the words beside it say, and so does
 * any answer that names a missed area, whatever its face (ops #72).
 */
export const FEEDBACK_FACES = values(['delighted', 'happy', 'so_so', 'unhappy']);
export type FeedbackFace = (typeof FEEDBACK_FACES)[number];

/**
 * The two faces that count as a good answer. Code only and not a CHECK: it is a reading of the four
 * above, and the first automatic charge (ops #46, step 4) waits for one of them.
 */
export const GOOD_FACES = values(['delighted', 'happy']);
export type GoodFace = (typeof GOOD_FACES)[number];

/**
 * The longest part of the home an answer may name, and the longest line of her words: held by the
 * door that takes the answer and by the page that offers the boxes, one number for both, and never a
 * CHECK, which could not be widened without a rebuild.
 */
export const FEEDBACK_AREA_MAX = 80;
export const FEEDBACK_LINE_MAX = 280;

/**
 * Where the question stands on one invoice, one `invoice_surveys` row each. `sent` when the invoice
 * message is handed on, `delivered` once the phone system says it arrived, `reminded` once the
 * reminder has gone, `call_raised` once the office has been asked to ring her, and `not_delivered`
 * when the message never could go: refused by consent, failed or cancelled. An answer or a hold is
 * a column beside the state, not a state, so the ladder reads one word and two stamps.
 */
export const SURVEY_STATES = values([
  'sent',
  'delivered',
  'reminded',
  'call_raised',
  'not_delivered',
]);
export type SurveyState = (typeof SURVEY_STATES)[number];

/**
 * Why the question is held, so nothing further happens on the invoice until a person releases it: an
 * answer below `happy` opened an issue; she replied in words a person has to read (ops #25, written
 * by nothing yet); or the office held it by hand.
 */
export const SURVEY_HOLD_REASONS = values(['issue_reported', 'reply_received', 'office_hold']);
export type SurveyHoldReason = (typeof SURVEY_HOLD_REASONS)[number];

/**
 * A cleaner at a locked door at 8am must reach an owner. One table, no routing engine.
 *
 * `issue_reported` is the seventh, added by migration `0006` because the damage, breakage and
 * safety notification had been borrowing `access_problem` since step 5: an owner reading her list
 * saw "could not get in" over a sentence about a broken lamp, and a count of access problems
 * counted every breakage as one. It took a rebuild to arrive because widening a CHECK on SQLite is
 * a twelve-step rebuild of a live table, and `0006` was the first one this table was part of.
 *
 * `kill_rule_fired` is the eighth, added by migration `0028` (ops #48, the owner, 2026-10-15): a
 * kill rule that fires tells both owners as well as drawing its line on Today, once a firing
 * (`reports/kill-rule-firings.ts`). None of the seven was true of it: nothing broke, nobody missed a
 * promise and nothing failed to go. A rule that fires is a decision for a person.
 *
 * `opt_out_hold` is the ninth, added by migration `0031` (ops #30, the owner, 2026-10-16): a text
 * whose words may ask us to stop texting is held, and when nobody has decided it in one business day
 * both owners are told, and again when five pass and her number is marked do not contact
 * (`schedule/stop-holds.ts`). A decision waiting for a person, as a rule that fires is.
 */
export const NOTIFICATION_KINDS = values([
  'access_problem',
  'issue_reported',
  'promise_breach',
  'task_due',
  'payment_late',
  'handoff_failed',
  'card_mismatch',
  'kill_rule_fired',
  'opt_out_hold',
]);
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/**
 * THE TITLE OF A HOLD'S SECOND NOTICE, the one copy (ops #33). A hold tells both owners twice under
 * the one kind `opt_out_hold`: at one business day undecided, when the Texts screen's band is where it
 * is decided, and at five, when her number has been marked and nothing waits there any more. The
 * notice names no row and `notifications` has no column for the step, so this fixed title is what
 * tells the two apart: the server writes it (`schedule/stop-holds.ts`, `suppressedWords`) and the
 * notifications screen reads it, to send nobody to a band that has nothing to decide.
 */
export const HOLD_BECAME_A_STOP_TITLE =
  'A stop in their words was never decided, so their number is now marked do not contact';

/**
 * EVERY TITLE THE SECOND NOTICE HAS BEEN WRITTEN WITH, which is what the screen reads. The title
 * became neutral on 2026-10-20 and a stored notice is never rewritten, so a notice written before
 * then still says "her" and is still the second notice. Read by the new title alone, it would be
 * taken for the first and send the owner to a band with nothing to decide.
 */
export const HOLD_BECAME_A_STOP_TITLES: readonly string[] = [
  HOLD_BECAME_A_STOP_TITLE,
  'A stop in her words was never decided, so her number is now marked do not contact',
];

export const NOTIFICATION_STATES = values(['unread', 'read', 'actioned']);
export type NotificationState = (typeof NOTIFICATION_STATES)[number];

export const TASK_KINDS = values(['renewal', 'filing', 'chase', 'admin', 'follow_up']);
export type TaskKind = (typeof TASK_KINDS)[number];

export const TASK_STATES = values(['open', 'doing', 'done', 'cancelled']);
export type TaskState = (typeof TASK_STATES)[number];

/**
 * The six promises from `architecture.md` section 5, as codes. Each is a stored definition plus a
 * query over timestamps that already exist, run nightly. Not an engine.
 */
export const PROMISE_CODES = values([
  'told_before_the_visit',
  'no_silent_change',
  'same_day_reply',
  'reclean_within_24h',
  'invoice_within_2_days',
  'price_matches_the_card',
]);
export type PromiseCode = (typeof PROMISE_CODES)[number];

/** `state` and `satisfied_by` so a threshold that fires once does not breach every night forever. */
export const PROMISE_STATES = values(['active', 'satisfied', 'paused']);
export type PromiseState = (typeof PROMISE_STATES)[number];

export const BREACH_STATES = values(['open', 'remedied', 'waived', 'sanctioned']);
export type BreachState = (typeof BREACH_STATES)[number];

/**
 * What cover exists, so a published sentence that depends on it can be checked. A lapse silently
 * converts true sentences on the website into false ones, which is the whole reason this table is
 * in wave one at zero clients.
 */
export const POLICY_KINDS = values([
  'general_liability',
  'fidelity_bond',
  'commercial_auto',
  'workers_compensation',
  'professional_liability',
]);
export type PolicyKind = (typeof POLICY_KINDS)[number];

export const POLICY_STATES = values(['none', 'quoted', 'bound', 'lapsed', 'expired']);
export type PolicyState = (typeof POLICY_STATES)[number];
